import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import * as THREE from 'three';
import {
  measurementKey as buildMeasurementKey,
  legacyElevationOffsetKey,
} from '@shared-lib/dimensioning';
import {
  isPolygonWall,
  getWallRightVector,
  getPolygonWallBounds,
  projectPolygonTo2D,
  getWallNormal,
  getWallPosition,
  getProjectionView,
  drawnFaceMidAlongUp,
} from '../../utils/elevationViewerHelpers';
import {
  calculateElevationMeasurements,
  createMeasurementSVGElements,
  createHiddenMeasurementSVGElements,
  layoutHiddenElevationMeasurements,
  layoutElevationMeasurements,
} from '../../utils/elevationMeasurementUtils';
import { isMeasurementHidden } from '../2d/elevationEditOps';
import {
  TIER_GEOMETRY,
  buildTieredElevationMeasurements,
} from '../../utils/elevationDimensionTiers';
import {
  fitStringsToScale,
  placeStringLabels,
} from '../../utils/elevationStringLabels';
import {
  CAPTURE_STROKE_PX,
  EMPHASIS_STROKE_FACTOR,
} from '../../utils/elevationPenWeights';
import { recordRenderStats } from './ElevationStats';
import {
  InlineSVGProjection,
  INLINE_PROJECTION_STYLE,
  getSvgCache,
} from './projectionSvg';
import { HIGHLIGHT_COLORS } from '../../constants/highlightColors';
import {
  resolveRoomOpening,
  isDemoedOpening,
} from '../../utils/openingSyncUtils';
import { DEMOED_HATCH_COLOR } from '../../constants/demoedStyle';
import { pickDefaultForType } from '../../services/glbModelHelpers';
import { getSchematicComponent } from './schematics';
import {
  getOccupancyGrid,
  buildBorderGrid,
  labelOverlapsOccupied,
} from '../../utils/svgOccupancyGrid';
import { objectsWithRunPreview } from '../../utils/elevationWallObjects';
import { dragCounterpart } from '../../utils/cabinetRunGeometry';
import {
  FIXED_MARKER,
  FIXED_MARKER_PX,
  isFixedCabinet,
} from '../../utils/fixedCabinetMarker';
import { isHungCeilingLight } from '../../utils/lightingClassification';
import { ceilingClipPath } from '../../utils/elevationCeilingClip';
import {
  wallFinishSpans,
  openingKindOf,
  placeWallFinish,
  bandHeightFrame,
} from '../../utils/elevationWallFinishes';
import WallFinishBandLayer, { BandGripLayer } from './WallFinishBandLayer';
import {
  cabinetTrimBandsByCarrier,
  countedPieceElevationBoxes,
  valanceElevationBoxes,
} from '../../utils/cabinetTrimElevation';
import { sideOnShadeSpan, cornerLegOnWall } from '../../utils/elevationSideOn';
import {
  elevationHandleGeometry,
  hitTestElevationHandle,
  arrowPath,
} from '../../utils/elevationGrabHandle';
import {
  bayWidthHandles,
  runEndHandles,
  legMoveHandle,
  paramToElevationX,
} from '../../utils/elevationRunHandles';
import {
  hitStackAt,
  resolveHitSelection,
  advanceHitCycle,
  EMPTY_CYCLE,
} from '../../utils/elevationHitStack';

/**
 * Build a human-readable label for an object in the elevation view.
 */
const getElevationLabel = (obj) => {
  if (obj.type === 'door' || obj.category?.door) {
    const doorCat =
      typeof obj.category?.door === 'object' ? obj.category.door : null;
    const doorType = doorCat?.doorType;
    if (doorType) {
      const readable = doorType
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/\bIn Swing\b/i, 'In-Swing')
        .replace(/\bOut Swing\b/i, 'Out-Swing')
        .trim();
      return `${readable} Door`;
    }
    return 'Door';
  }
  if (obj.type === 'window' || obj.category?.window) {
    const winCat =
      typeof obj.category?.window === 'object' ? obj.category.window : null;
    const windowType = winCat?.windowType;
    if (windowType) {
      const readable = windowType
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
      return `${readable} Window`;
    }
    return 'Window';
  }
  // Cased openings / archways: no type, just category.opening.
  if (obj.type === 'opening' || obj.category?.opening) {
    return 'Opening';
  }
  // Resolve a stringly typeKey from whichever field actually carries it.
  // obj.type can be a string ('toilet'), a single-key map ({toilet: ...}),
  // or undefined. obj.category is usually an object ({ door: {...} }, etc.)
  // — extract its first key when type is missing.
  let typeKey;
  if (typeof obj.type === 'string' && obj.type) {
    typeKey = obj.type;
  } else if (typeof obj.type === 'object' && obj.type) {
    typeKey = Object.keys(obj.type)[0];
  } else if (typeof obj.category === 'object' && obj.category) {
    typeKey = Object.keys(obj.category)[0];
  } else if (typeof obj.category === 'string') {
    typeKey = obj.category;
  }
  if (typeof typeKey === 'string' && typeKey) {
    return typeKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
  }
  return 'Object';
};

/**
 * Check if two axis-aligned rectangles overlap.
 * Each rect is { x, y, w, h } where (x, y) is the top-left corner.
 */
const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/**
 * Compute non-overlapping label placements for all objects.
 *
 * Strategy per label (in priority order):
 *   1. Centered on the element
 *   2. Immediately below the element
 *   3. Immediately above the element
 *   4+ Progressively farther below / above (draws a leader line)
 *
 * @param {Map} [occupancyGrids] - Map of objId → occupancy grid for SVG-aware overlap checking
 * @returns Array of { id, label, x, y, anchorX, anchorY, needsLine }
 */
const computeLabelPlacements = (
  projectedObjects,
  scale,
  isPreview,
  occupancyGrids
) => {
  const fontSize = isPreview ? 8 : 11;
  const charWidth = fontSize * 0.55;
  const labelH = fontSize + 6;
  const gap = 4; // spacing between element edge and label

  // Collect occupied rects: start with all object bounding boxes
  const occupied = [];
  const objRects = projectedObjects.map((obj) => {
    const isDoor = obj.category?.door || obj.type === 'door';
    const isWindow = obj.category?.window || obj.type === 'window';
    const w =
      obj.renderedWidth || (isDoor ? 0.9 : isWindow ? 1.2 : 0.5) * scale || 50;
    const h =
      obj.renderedHeight || (isDoor ? 2.1 : isWindow ? 1.5 : 0.5) * scale || 50;
    const rect = { x: obj.x2D - w / 2, y: obj.y2D - h / 2, w, h };
    occupied.push(rect);
    return { obj, rect, w, h };
  });

  const placements = [];
  const objCount = objRects.length; // first objCount entries in occupied are object rects

  for (let i = 0; i < objRects.length; i++) {
    const { obj, w, h } = objRects[i];
    const label = getElevationLabel(obj);
    const labelW = label.length * charWidth + 8;
    const cx = obj.x2D; // center x of element
    const cy = obj.y2D; // center y of element
    const halfH = h / 2;

    // Build candidate positions: [y, needsLine]
    // Try positions within the bounding box first, then outside
    const candidates = [
      { y: cy - labelH / 2, line: false }, // center of element
      { y: cy - halfH * 0.5 - labelH / 2, line: false }, // upper quarter
      { y: cy + halfH * 0.5 - labelH / 2, line: false }, // lower quarter
      { y: cy + halfH + gap, line: false }, // immediately below
      { y: cy - halfH - gap - labelH, line: false }, // immediately above
    ];
    // Progressively farther positions (with leader line)
    for (let step = 1; step <= 5; step++) {
      const offset = labelH * step + gap * (step + 1);
      candidates.push({ y: cy + halfH + offset, line: true }); // below
      candidates.push({ y: cy - halfH - offset - labelH, line: true }); // above
    }

    let placed = false;
    for (const { y: candY, line } of candidates) {
      const labelRect = {
        x: cx - labelW / 2,
        y: candY,
        w: labelW,
        h: labelH,
      };
      // Check label against all occupied rects (other objects, own element, placed labels).
      // For objects with SVG grids, allow placement in empty whitespace.
      // Own element uses a lenient threshold (label can slightly overlap its edges).
      const overlaps = occupied.some((r, ri) => {
        if (ri < objCount && occupancyGrids) {
          const grid = occupancyGrids.get(objRects[ri].obj.id);
          if (grid) {
            if (!rectsOverlap(r, labelRect)) return false;
            // Own element: lenient (allow up to 20% overlap with drawn lines)
            // Neighbors: strict (any overlap with drawn content is rejected)
            const thresh = ri === i ? 0.2 : 0;
            return labelOverlapsOccupied(labelRect, r, grid, thresh);
          }
          // Own element without a grid: allow overlap (original behavior)
          if (ri === i) return false;
        }

        // Skip own element when no grids available at all
        if (ri === i) return false;

        // Fallback to AABB for placed labels and objects without grids
        return rectsOverlap(r, labelRect);
      });
      if (!overlaps) {
        // Determine leader line anchor point (closest element edge to label)
        let anchorY;
        if (candY + labelH / 2 > cy) {
          anchorY = cy + halfH; // label is below → line from bottom edge
        } else {
          anchorY = cy - halfH; // label is above → line from top edge
        }
        placements.push({
          id: obj.id,
          label,
          x: cx,
          y: candY + labelH / 2, // center y of label
          labelW,
          anchorX: cx,
          anchorY,
          needsLine: line,
        });
        occupied.push(labelRect);
        placed = true;
        break;
      }
    }

    if (!placed) {
      // Force-place below everything
      const maxY = Math.max(...occupied.map((r) => r.y + r.h), 0);
      const y = maxY + gap;
      placements.push({
        id: obj.id,
        label,
        x: cx,
        y: y + labelH / 2,
        labelW,
        anchorX: cx,
        anchorY: cy + halfH,
        needsLine: true,
      });
      occupied.push({ x: cx - labelW / 2, y, w: labelW, h: labelH });
    }
  }

  return placements;
};

// Cache for fetched SVG content (raw)
// A wall seen end-on is shaded, not drawn as an outline: it is the SECTION
// through that wall, and the grey is the drawing's own poché. Kept lighter
// than an object's stroke so it reads as background to what stands on the
// wall, and matched to the cased-opening wash the same drawing already uses.
const END_ON_BAND_FILL = 'rgba(120, 120, 120, 0.18)';
const END_ON_BAND_STROKE = '#333';
// Casework seen SIDE-ON takes the same poché, laid on the wall face's own
// white: the object stands in front of the wall, so a finish's hatch behind it
// is hidden rather than showing through, and several side-on cabinets stacked
// in depth read as one shade instead of darkening with each layer.
const SIDE_ON_SHADE_BACKGROUND = {
  backgroundColor: '#ffffff',
  backgroundImage: `linear-gradient(${END_ON_BAND_FILL}, ${END_ON_BAND_FILL})`,
};

// A finish laid ON the wall face (item #1) is drawn by `WallFinishBandLayer`,
// which also lets a Backsplash-card band be picked (item #28).
//
// The label is set a step below the measurement text, so it scales with the
// drawing's own text: the capture page sizes `measurementFontSize` for print,
// and a fixed px label would shrink to nothing once a capture is placed on a
// PDF page.
// Cabinet trim is drawn as the cabinets are: an outline in the objects'
// stroke with a faint fill, so it reads as part of the casework.
const CABINET_TRIM_BAND_STYLE = {
  boxSizing: 'border-box',
  border: '1px solid rgba(60, 60, 60, 0.85)',
  background: 'rgba(60, 60, 60, 0.06)',
};

/**
 * The part of a box drawn front-on, as fractions of its width, given what of
 * it is turned across the line of sight (`sideOnShadeSpan`); null when all of
 * it is.
 */
const frontOnSpan = (sideOn) => {
  if (!sideOn) return { from: 0, to: 1 };
  if (sideOn.from <= 0.001 && sideOn.to >= 0.999) return null;
  if (sideOn.from <= 0.001) return { from: sideOn.to, to: 1 };
  if (sideOn.to >= 0.999) return { from: 0, to: sideOn.from };
  return { from: 0, to: 1 };
};

const wallFinishLabelPx = (measurementFontSize) =>
  Math.round((measurementFontSize || 14) * 0.8);

export { wallFinishBackground } from './WallFinishBandLayer';

/**
 * The edge projections to draw for an object, or `null` to draw none.
 *
 * The type DEFAULT is the fallback for an object that names NO model — a
 * scanned fixture nobody has picked a product for. It must never stand in for
 * a model that IS named but whose projection has not loaded yet, because that
 * draws a DIFFERENT PRODUCT'S outline and is indistinguishable from success.
 *
 * That is exactly what a fixture swap hit. The elevations catalog is scoped to
 * the model ids the scan already references (`scanModelIds` in
 * StructureViewerWrapper), so for one render pass after a swap the object
 * carries a modelId the catalog has not fetched. Both lookups missed, the type
 * default stood in — which is the very model the vanity builder installed —
 * and it was drawn at the NEW object's dimensions. Reported as "a small
 * version of the original faucet"; both halves of that came from this one
 * substitution, and a refresh "fixed" it because the refetch had landed.
 *
 * Returning null lets the caller fall through to the procedural schematic,
 * which is generic and honest about not knowing the product.
 */
export const projectionsForObject = (
  obj,
  objectProjections,
  glbObjects,
  typeKey
) => {
  // A HUNG light never draws from its GLB, however well its projection has
  // loaded. Catalog pendants are modelled at FULL cord — the one on dev's
  // kitchens is a 68" bbox hanging 38" — and the scene cuts that to length by
  // shifting the stem band's vertices per object (`applyMaterialRegions`,
  // `stemLengthInches`). A baked SVG silhouette cannot be cut, so the
  // projection would print the uncut fixture, a foot and a half of it below
  // where the real one ends. The schematic draws the hang the object states.
  if (isHungCeilingLight(obj)) return null;
  const modelId = obj.modelId;
  if (modelId) {
    const cleanModelId = modelId.startsWith('saved_')
      ? modelId.substring(6)
      : modelId;
    const named =
      objectProjections?.get(modelId) ||
      objectProjections?.get(cleanModelId) ||
      null;
    if (named) return named;
    if (obj.modelUrl && glbObjects?.[typeKey]) {
      const glbObj = glbObjects[typeKey].find(
        (g) => g.url === obj.modelUrl || g.glbURL === obj.modelUrl
      );
      if (glbObj?.edgeProjections) return glbObj.edgeProjections;
    }
    // Named a product we cannot draw yet: draw no projection rather than
    // another product's.
    return null;
  }
  if (typeKey && glbObjects?.[typeKey]) {
    const defaultModel = pickDefaultForType(glbObjects[typeKey]);
    if (defaultModel?.edgeProjections) return defaultModel.edgeProjections;
  }
  return null;
};

/**
 * The catalog record an object names, or null.
 *
 * A schematic normally needs nothing but the object — it draws a generic
 * shape at the object's size. A hung light is the exception: its PROFILE
 * (canopy / stem / shade, each with its own extent and radius) lives in the
 * catalog's `materialRegions`, and the elevations bucket carries the whole
 * document, so the drawing can read it.
 *
 * Deliberately by id / url only, NOT by the projection-URL match the drawing
 * path uses: that match exists to recover a record when the id has not landed
 * yet, and here a miss simply means the schematic draws its fallback.
 */
export const catalogModelForObject = (obj, glbObjects, typeKey) => {
  const bucket = typeKey ? glbObjects?.[typeKey] : null;
  if (!bucket?.length || !obj) return null;
  const modelId = obj.modelId;
  const cleanModelId = modelId?.startsWith('saved_')
    ? modelId.substring(6)
    : modelId;
  return (
    bucket.find(
      (m) =>
        (modelId && (m.id === modelId || m.id === cleanModelId)) ||
        (obj.modelUrl && (m.url === obj.modelUrl || m.glbURL === obj.modelUrl))
    ) || null
  );
};

/**
 * Resolve the SVG projection URL for an object without performing dimension
 * calculations or rendering. Used by the label-placement pass to build
 * occupancy grids from cached SVGs.
 *
 * @returns {string|null} The projection SVG URL, or null if unavailable.
 */
const resolveObjectSvgUrl = (obj, objectProjections, glbObjects, wallData) => {
  const objType = obj.type || obj.category;
  const modelId = obj.modelId;

  const typeKey =
    typeof objType === 'object' && objType ? Object.keys(objType)[0] : objType;
  const projections = projectionsForObject(
    obj,
    objectProjections,
    glbObjects,
    typeKey
  );

  if (!projections || !projections.front) return null;

  // Determine which view to show
  const wallNormal = getWallNormal(wallData);

  let objRotX = 0,
    objRotY = 0,
    objRotZ = 0;
  if (obj.rotation) {
    if (Array.isArray(obj.rotation)) {
      [objRotX, objRotY, objRotZ] = obj.rotation;
    } else if (typeof obj.rotation === 'object') {
      objRotX = obj.rotation.x || 0;
      objRotY = obj.rotation.y || 0;
      objRotZ = obj.rotation.z || 0;
    }
  }
  const maxObjRot = Math.max(
    Math.abs(objRotX),
    Math.abs(objRotY),
    Math.abs(objRotZ)
  );
  if (maxObjRot > 2 * Math.PI) {
    objRotX = THREE.MathUtils.degToRad(objRotX);
    objRotY = THREE.MathUtils.degToRad(objRotY);
    objRotZ = THREE.MathUtils.degToRad(objRotZ);
  }
  const objRotation = new THREE.Euler(objRotX, objRotY, objRotZ);

  const viewName = getProjectionView(wallNormal, objRotation);
  const projection = projections[viewName] || projections.front;

  return projection?.url || null;
};

/**
 * Component for rendering a wall elevation with objects, doors, and windows
 */
const RenderWallElevation = ({
  wallData,
  index,
  isPrimary = false,
  isPreview = false,
  previewScale = null, // If provided, use this scale for preview tiles (pixels per meter)
  zoomScale = 1,
  showMeasurements,
  measurementFilter,
  selectedWallGroup,
  selectedObject,
  selectedObjectId,
  highlightedObjectIds = [], // Objects to highlight in red (e.g., from selected measurement)
  onObjectSelect,
  setSelectedObject,
  // A host that shows the object's details in the SHARED slide-in panel needs
  // no copy of the object data of its own, so this is optional. Two render-only
  // call sites (the PDF capture paths) already passed `null` for it and only
  // escaped a crash because their click handlers never fire.
  setSelectedObjectData = () => {},
  setSelectedMeasurementData,
  selectedMeasurementData,
  removedMeasurements = [],
  savedMeasurementOffsets = {}, // Offsets loaded from Firebase, keyed by measurement key
  onMeasurementOffsetSave, // Callback to save offset: (measurementKey, offset) => void
  objectProjections,
  // individualObjectModels prop removed - objects have modelId directly
  glbObjects,
  primaryWallIndex,
  isPanning = false,
  measurementFontSize = null, // Override measurement label font size (default: isPreview ? 10 : 14)
  showLabels = false,
  // When ON, a demoed opening (category.opening.demoed === true) draws with a
  // red 45° diagonal hatch (matching the floorplan + 3D demoed indication).
  // When OFF, it renders as a normal opening (no hatch) — the after-demolition
  // / removed-wall reading. Defaults true so the standalone page + capture
  // paths stay consistent with the other renderers' demoed defaults.
  showDemoedWalls = true,
  // Optional Map<structureOpeningId, structureOpening>. When provided,
  // any door/window in nearbyObjects with a structureOpeningId is
  // resolved against its structure counterpart for opening-itself fields
  // (doorType, slidingPanelCount, wallFacesPriorityRoom) and coords. Items
  // without an FK or callers that don't pass the lookup are unchanged.
  structureOpeningLookup = null,
  // When ON, the measurements the user has HIDDEN on this wall are also drawn,
  // as grey dashed ghosts with a crossed-out eye. The Hidden Measurements panel
  // turns this on while it is open, so "hidden" is legible on the drawing
  // itself and not just as a list of endpoint names.
  showHiddenMeasurements = false,
  // --- Cabinet-run bay drag (the primary wall only) ------------------------
  // Slide a run-anchored appliance along its run by dragging it sideways. The
  // pixel maths has to live here because `scale` (px per metre) is derived
  // below and never leaves this component; everything about the RUN stays in
  // the host, which is why these hand up a bare along-wall distance in metres
  // rather than a bay, a param or a world point.
  //
  // `alongM` is signed by SCREEN-RIGHT, the basis this component draws with
  // (`wallRight` below). The host reconciles it with the run's own `wallDir`,
  // which for this codebase's data sources is parallel but not always
  // same-signed — see `bayMovementFrame`.
  objectInteractionMode = 'select',
  canDragObjectAlongRun = null, // (object) => boolean
  // The RUN's drag handles for the selected object, from the host — which
  // scope is open, where the run's ends and a free leg's midpoint sit (as
  // PARAMS, since nothing is drawn at them), and one reference param so this
  // view can map params to its own pixels. Run knowledge stays in the host;
  // pixels stay here, the same split `elevationBayDrag` documents.
  // { scope, ends: [{end, param, pinned, reason}], legMidParam, refParam, sign }
  runHandleModel = null,
  // The rest of the run while the RUN panel is open — pink, as the plan and
  // the 3D scene paint it (`utils/runHighlightScope`).
  runScopeIds = [],
  onBayDragStart = null, // (object, handle) => void
  onBayDragMove = null, // (alongM) => void
  onBayDragEnd = null, // (alongM) => void
  // (cabinet) => void — a click on a fixed cabinet's pin releases it (the
  // host writes Width → Auto). Without it the pins are drawn but inert.
  onUnpinCabinet = null,
  // The host's live ghost: `{ objects, hideIds }` from `previewForRunEdit`.
  runPreview = null,
  // Told how many objects a click landed on, when it is more than one, so the
  // host can say that clicking again reaches the next one. Cycling is not
  // discoverable on its own — nothing on the drawing says there is anything
  // underneath.
  onHitStackNotice = null,
  // Is a FINGER the pointer (`useCoarsePointer`, resolved by the host)? Turns
  // on the touch drag of an object's body; false keeps the mouse layout that
  // shipped before, which is also what jsdom reports. The MOVE handle is
  // drawn for either pointer.
  coarsePointer = false,
  // --- Backsplash / tile bands (call item #28; the primary wall only) ------
  // Which card each drawn wall has — a Map of wall id → `{roomId, kind}`
  // (`utils/elevationBands.elevationBandTargets`) — so a span can say which
  // card band it is; and the host's hover / pick handlers
  // (`{selected, hovered, onHover, onSelect}`). Both default to null, and
  // every host but the viewer's editable elevation passes neither.
  bandTargets = null,
  bandInteraction = null,
  // A band's top edge being dragged: the planned regions of each wall the
  // drag would change (`Map` of wall id → regions), drawn in place of the
  // stored ones while it lasts.
  previewWallRegions = null,
}) => {
  const {
    id,
    roomName,
    nearbyObjects: committedObjects = [],
    isGrouped,
    groupedWalls,
  } = wallData;

  // The run's ghost replaces the run's committed boxes wholesale — a re-solve
  // regenerates their ids, so this cannot be a merge. Identity is preserved
  // when there is no preview, which is every frame outside a drag.
  const nearbyObjects = useMemo(
    () => objectsWithRunPreview(wallData, committedObjects, runPreview),
    [wallData, committedObjects, runPreview]
  );
  // The selection's stand-in in that ghost: a re-solve mints new ids, so the
  // selected box would otherwise stop being highlighted the moment a drag
  // starts, and nothing on screen said what was moving (user report
  // 2026-10-26). Its handles follow it too.
  const previewSelectedId = useMemo(() => {
    if (!runPreview?.objects?.length) return null;
    const selectedId = selectedObjectId || selectedObject || null;
    const selected = selectedId
      ? committedObjects.find((o) => o?.id === selectedId)
      : null;
    return dragCounterpart(runPreview.objects, selected)?.id ?? null;
  }, [runPreview, selectedObjectId, selectedObject, committedObjects]);
  // While a drag is previewed the selection IS its stand-in, and nothing else
  // is. The re-solve mints ids from where each box starts, so the box built
  // where the selection used to start carries the selection's own id — and a
  // match on that id painted the new cabinet in the dragged sink's wake pink
  // as well (user report 2026-10-26).
  const isSelectedId = (objId) =>
    previewSelectedId != null
      ? previewSelectedId === objId
      : selectedObject === objId || selectedObjectId === objId;

  // State for dragging measurements
  const [draggedMeasurement, setDraggedMeasurement] = useState(null);
  const [localOffsets, setLocalOffsets] = useState({}); // Only stores offsets changed during this session
  const svgRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Clear local offsets when saved offsets are reset (empty object)
  const savedOffsetsRef = useRef(savedMeasurementOffsets);
  useEffect(() => {
    const prevHadOffsets =
      Object.keys(savedOffsetsRef.current || {}).length > 0;
    const currentHasOffsets =
      Object.keys(savedMeasurementOffsets || {}).length > 0;

    // If saved offsets went from having values to being empty, clear local offsets
    if (prevHadOffsets && !currentHasOffsets) {
      setLocalOffsets({});
    }
    savedOffsetsRef.current = savedMeasurementOffsets;
  }, [savedMeasurementOffsets]);

  // Track mouse position to distinguish clicks from drags
  const mouseDownPos = useRef(null);
  const CLICK_THRESHOLD = 5; // pixels - if mouse moves more than this, it's a drag

  // --- Picking one object out of a stack -----------------------------------
  //
  // Every object is a plain div with no z-index, so the browser hands the
  // click to whatever was painted last and anything under it is unreachable.
  // The rule (smallest first, keep the selection, cycle on a repeat click)
  // lives in `utils/elevationHitStack`; what is here is the wiring.
  const hitCycleRef = useRef(EMPTY_CYCLE);
  const [touchDragging, setTouchDragging] = useState(false);
  const pendingPickRef = useRef(null);
  // `projectedObjects` is derived below, so the handlers reach it by ref.
  const projectedRef = useRef([]);
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedObjectId || selectedObject || null;

  const objectById = useCallback(
    (id) => projectedRef.current.find((o) => o?.id === id) || null,
    []
  );

  /**
   * What a press at this point refers to — PURE, so the pan guard can ask the
   * same question the press will answer without advancing the cycle.
   */
  const resolveAt = useCallback(
    (e, fallback) => {
      const hits = hitStackAt(e.clientX, e.clientY).map((h) => h.id);
      const res = resolveHitSelection({
        hits,
        selectedId: selectedIdRef.current,
        cycle: hitCycleRef.current,
      });
      return { res, target: objectById(res.id) || fallback };
    },
    [objectById]
  );

  /** The same, committing the cycle state — for the press itself. */
  const pickAt = useCallback(
    (e, fallback) => {
      const { res, target } = resolveAt(e, fallback);
      hitCycleRef.current = res.cycle;
      pendingPickRef.current = res;
      return target;
    },
    [resolveAt]
  );

  // --- The bay drag ---------------------------------------------------------
  //
  // `scale` is derived further down (it depends on the wall's own size), so
  // the event handlers read it through a ref rather than a closure. The whole
  // conversion is one number: screen pixels ÷ (px per metre × zoom).
  const pxPerMetreRef = useRef(1);
  const bayDrag = useRef(null);

  // Could this object travel along its run at all — the question the MOVE
  // handle asks, with no mode in front of it (a press on the handle is
  // unambiguous). A press on the object's BODY could equally mean pan or
  // select, so that one still waits for "Move along run" (`canBayDrag`).
  const canMoveAlongRun = useCallback(
    (obj) =>
      !isPreview && !isPanning && !!obj && !!canDragObjectAlongRun?.(obj),
    [isPreview, isPanning, canDragObjectAlongRun]
  );

  const canBayDrag = useCallback(
    (obj) => objectInteractionMode === 'move' && canMoveAlongRun(obj),
    [objectInteractionMode, canMoveAlongRun]
  );

  // The start is passed IN rather than read off the ref, because mouseup
  // clears the ref before it reports the distance — reading it there gave zero
  // travel for every drag, which commits the bay back where it started.
  // Refs otherwise only, so the document-level handlers never go stale.
  const alongMetres = useCallback(
    (clientX, startClientX) =>
      (clientX - startClientX) / (pxPerMetreRef.current || 1),
    []
  );

  // --- one drag, two input devices -----------------------------------------
  //
  // A finger produces no mousemove, so touch cannot ride the mouse path. It
  // does not need to: this gesture is ONE number (horizontal travel), so the
  // two paths share an arm / move / end trio rather than the plan's replay of
  // synthetic mouse events. Nothing here needs the plan's promotion offset or
  // its lift — a bay drag reads the pointer ALONG the wall, and the plan
  // excludes it from the lift for exactly that reason.

  /**
   * @param {Object|null} handle  one of the RUN's handles (an edge or an end)
   * @param {boolean} viaMoveHandle  the press landed on the MOVE handle, which
   *   drags the object itself along its run
   * @returns true when a drag is now PENDING on `target`.
   */
  const armBayDrag = useCallback(
    (target, clientX, handle = null, viaMoveHandle = false) => {
      // A HANDLE needs no mode in front of it — grabbing one is unambiguous,
      // and only a drag on the object's BODY could equally mean pan or select.
      // The same rule the floor plan follows; the handle only exists at all
      // because the host offered it for this object and scope.
      const ok = handle
        ? !isPreview && !isPanning
        : viaMoveHandle
          ? canMoveAlongRun(target)
          : canBayDrag(target);
      if (!ok) {
        bayDrag.current = null;
        return false;
      }
      bayDrag.current = {
        object: target,
        handle,
        startClientX: clientX,
        armed: false,
      };
      return true;
    },
    [canBayDrag, canMoveAlongRun, isPreview, isPanning]
  );

  /** @returns true while a press is pending or dragging — the caller stops there. */
  const moveBayDrag = useCallback(
    (clientX) => {
      const d = bayDrag.current;
      if (!d) return false;
      if (!d.armed) {
        if (Math.abs(clientX - d.startClientX) <= CLICK_THRESHOLD) return true;
        d.armed = true;
        onBayDragStart?.(d.object, d.handle);
      }
      onBayDragMove?.(alongMetres(clientX, d.startClientX), d.handle);
      return true;
    },
    [onBayDragStart, onBayDragMove, alongMetres]
  );

  /** @returns true if a drag actually RAN, so the caller skips its click. */
  const endBayDrag = useCallback(
    (clientX) => {
      const d = bayDrag.current;
      bayDrag.current = null;
      if (!d?.armed) return false;
      onBayDragEnd?.(
        alongMetres(clientX ?? d.startClientX, d.startClientX),
        d.handle
      );
      return true;
    },
    [onBayDragEnd, alongMetres]
  );

  // A finger drag runs on TOUCH events, which a browser does not follow with
  // mousemove — so the listeners are its own, registered only while a touch
  // drag is live. The trio above is shared, so the two devices cannot disagree
  // about what a drag means.
  const touchDragRef = useRef(null);
  useEffect(() => {
    if (!touchDragging) return undefined;
    const move = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      // The finger is dragging the object, not the page.
      if (e.cancelable) e.preventDefault();
      moveBayDrag(t.clientX);
    };
    const end = (e) => {
      const t = e.changedTouches?.[0];
      endBayDrag(t?.clientX);
      setTouchDragging(false);
    };
    // Not passive: a bay drag has to be able to stop the page scrolling under
    // it, and a passive listener may not call preventDefault.
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', end);
    return () => {
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', end);
    };
  }, [touchDragging, moveBayDrag, endBayDrag]);

  const handleObjectTouchStart = useCallback(
    (e, obj) => {
      if (!coarsePointer || isPreview) return;
      const t = e.touches?.[0];
      if (!t) return;
      const target = pickAt({ clientX: t.clientX, clientY: t.clientY }, obj);
      if (!armBayDrag(target, t.clientX)) return;
      // Stop the viewport pinch/pan hook, which is on POINTER events and has
      // already claimed this finger.
      e.stopPropagation();
      touchDragRef.current = target;
      setTouchDragging(true);
    },
    [coarsePointer, isPreview, pickAt, armBayDrag]
  );

  /**
   * A press on one of the RUN's handles.
   *
   * The object it arms against is the SELECTED one — a handle belongs to the
   * selection, not to whatever div sits under the pointer, so there is no
   * `pickAt` here. The handle itself rides along so the host knows which edit
   * this is; the 5 px arm threshold and the mouse/touch split are the body
   * drag's, shared rather than re-implemented.
   */
  const handleRunHandleMouseDown = useCallback(
    (e, handle) => {
      const target = objectById(selectedIdRef.current);
      if (!target) return;
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
      if (!armBayDrag(target, e.clientX, handle)) return;
      e.stopPropagation();
    },
    [armBayDrag, objectById]
  );

  const handleRunHandleTouchStart = useCallback(
    (e, handle) => {
      const t = e.touches?.[0];
      const target = objectById(selectedIdRef.current);
      if (!t || !target) return;
      if (!armBayDrag(target, t.clientX, handle)) return;
      // Stop the viewport pinch/pan hook, which is on POINTER events and has
      // already claimed this finger — the same guard the body drag carries.
      e.stopPropagation();
      touchDragRef.current = target;
      setTouchDragging(true);
    },
    [armBayDrag, objectById]
  );

  /**
   * A press on the MOVE handle under the selected object. Like a run handle it
   * belongs to the selection, so it arms against the selected object rather
   * than whatever `pickAt` finds under the pointer — the handle stands below
   * the object, over whatever is drawn there.
   */
  const handleMoveHandleMouseDown = useCallback(
    (e) => {
      const target = objectById(selectedIdRef.current);
      if (!target) return;
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
      if (!armBayDrag(target, e.clientX, null, true)) return;
      e.stopPropagation();
    },
    [armBayDrag, objectById]
  );

  const handleMoveHandleTouchStart = useCallback(
    (e) => {
      const t = e.touches?.[0];
      const target = objectById(selectedIdRef.current);
      if (!t || !target) return;
      if (!armBayDrag(target, t.clientX, null, true)) return;
      e.stopPropagation();
      touchDragRef.current = target;
      setTouchDragging(true);
    },
    [armBayDrag, objectById]
  );

  const handleObjectMouseDown = useCallback(
    (e, obj) => {
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
      // WHICH object this press refers to is not "the div that received it" —
      // that is only the top of the stack. Resolve it before anything else,
      // because the drag must arm against the object the click will select.
      const target = isPreview ? obj : pickAt(e, obj);
      // Pending, not started: a press that never travels is a CLICK, and a
      // click must select rather than cost a run re-solve and a write.
      if (!armBayDrag(target, e.clientX)) return;
      // The CONTAINER carries this same handler (with no object) so that a
      // press on the background clears a stale drag — and a press on an object
      // bubbles to it. Without this the drag was armed and then immediately
      // unarmed by the parent, one event later, and nothing moved.
      // Safe to stop: the container's mousedown only records the position that
      // this handler has already recorded, and an object's own onClick already
      // stops the deselect click from reaching it.
      e.stopPropagation();
    },
    [armBayDrag, pickAt, isPreview]
  );

  // The viewport pans on POINTER events and this drag is on MOUSE events, and
  // `stopPropagation` on a mousedown does not stop the pointerdown that has
  // already fired — so both gestures would run at once and the drawing would
  // slide out from under the cursor (see `stopCanvasPan`, the same guard every
  // measurement carries). Only while a drag is actually armable: an
  // unconditional guard here would kill drag-to-pan that begins over an
  // object, which is most of the drawing.
  const handleObjectPointerDown = useCallback(
    (e, obj) => {
      if (isPreview) return;
      // Ask about the object the press will DRAG, not the div that received
      // the event — that div is only the top of the stack. A fridge under its
      // surround is the draggable one while the surround takes the pointer,
      // so a guard on the div let the canvas pan run alongside the drag
      // (user-reported: "the canvas also slides too").
      if (canBayDrag(resolveAt(e, obj).target)) e.stopPropagation();
    },
    [canBayDrag, resolveAt, isPreview]
  );

  const isClickNotDrag = useCallback((e) => {
    if (!mouseDownPos.current) return true;
    const dx = Math.abs(e.clientX - mouseDownPos.current.x);
    const dy = Math.abs(e.clientY - mouseDownPos.current.y);
    return dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD;
  }, []);

  /**
   * Select what a click on an object refers to.
   *
   * ONE body for both object render paths (projection-drawn and
   * schematic/box) — they were byte-identical copies, and a selection rule
   * that lives in two places is one that will end up meaning two things.
   *
   * The cycle ADVANCES here rather than on the press: on mousedown the press
   * might still become a drag, and moving the selection then would change
   * what is being dragged half-way through the gesture.
   */
  const handleObjectClick = useCallback(
    (e, obj) => {
      if (isPanning || isPreview || !isClickNotDrag(e)) return;
      e.stopPropagation();

      const pending = pendingPickRef.current;
      let target = obj;
      if (pending?.isContinuation) {
        const next = advanceHitCycle(hitCycleRef.current);
        hitCycleRef.current = next.cycle;
        target = objectById(next.id) || obj;
      } else if (pending?.id) {
        target = objectById(pending.id) || obj;
      }

      setSelectedObject(target.id);
      setSelectedObjectData(target);
      // Optional: a host that lets no measurement be picked (the homeowner's
      // cabinet editor) passes none, which is also what keeps measurements
      // unclickable (`onMeasurementClick` below).
      setSelectedMeasurementData?.(null);
      if (onObjectSelect) {
        const roomId =
          target.roomId || wallData.roomId || target.associatedWall?.roomId;
        onObjectSelect({ ...target, roomId, id: target.id });
      }
      // Once per stack, on the way IN. Repeating it on every step round the
      // cycle would be telling someone what they are already doing.
      if (pending?.count > 1 && !pending.isContinuation) {
        onHitStackNotice?.(pending.count);
      }
    },
    [
      isPanning,
      isPreview,
      isClickNotDrag,
      objectById,
      setSelectedObject,
      setSelectedObjectData,
      setSelectedMeasurementData,
      onObjectSelect,
      onHitStackNotice,
      wallData,
    ]
  );

  // Merge saved offsets with local offsets (local takes precedence during drag)
  const measurementOffsets = { ...savedMeasurementOffsets, ...localOffsets };

  // Stable key for a measurement: its two endpoints as typed refs, the same
  // vocabulary the link graph uses (`@shared-lib/dimensioning`). This is what
  // an offset is SAVED under.
  // In a tiered string every segment shares ONE line, so a drag moves the
  // string, not the segment — dragging one segment off its line would break
  // the chain. `all` mode keeps its per-measurement key.
  const getMeasurementKey = useCallback(
    (measurement) =>
      measurement?.stringKey ||
      buildMeasurementKey(measurement, { wallId: wallData?.id }),
    [wallData?.id]
  );

  // Read an offset, honouring keys written before the ref keys existed. The
  // old key embedded the endpoint LABELS, and `showNumber` renames
  // "Cabinet Left" to "Cabinet 1 Left" as soon as a second cabinet lands on
  // the wall — so reading by the new key alone would drop every offset a user
  // has already saved. Nothing writes the legacy form any more; it is read
  // until the next save moves the entry over.
  const offsetFor = useCallback(
    (measurement, fallback) => {
      const key = getMeasurementKey(measurement);
      if (key && measurementOffsets[key] !== undefined) {
        return measurementOffsets[key];
      }
      const legacyKey = legacyElevationOffsetKey(measurement);
      if (legacyKey && measurementOffsets[legacyKey] !== undefined) {
        return measurementOffsets[legacyKey];
      }
      return fallback;
    },
    [getMeasurementKey, measurementOffsets]
  );

  // Drag handlers for measurements
  // Track potential drag - don't start dragging until mouse moves beyond threshold
  const pendingDrag = useRef(null);
  const DRAG_THRESHOLD = 5; // pixels

  const handleMeasurementMouseDown = useCallback(
    (e, measurement, index) => {
      if (isPreview || isPanning) return;

      e.stopPropagation();

      const svg = svgRef.current;
      if (!svg) return;

      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPoint = pt.matrixTransform(svg.getScreenCTM().inverse());

      const measurementKey = getMeasurementKey(measurement);
      const initialOffset = offsetFor(measurement, measurement.offset || 0);

      // Store pending drag info - don't start dragging yet
      pendingDrag.current = {
        measurement,
        index,
        measurementKey,
        startX: svgPoint.x,
        startY: svgPoint.y,
        startClientX: e.clientX,
        startClientY: e.clientY,
        initialOffset,
      };
    },
    [isPreview, isPanning, offsetFor, getMeasurementKey]
  );

  // Drag handler for labels - same as measurement lines
  const handleLabelMouseDown = useCallback(
    (e, measurement, index) => {
      // Use the same handler as measurement lines
      handleMeasurementMouseDown(e, measurement, index);
    },
    [handleMeasurementMouseDown]
  );

  const handleMouseMove = useCallback(
    (e) => {
      // A bay drag in flight (or one deciding whether it is a drag at all).
      // Only the HORIZONTAL travel counts: the appliance's one degree of
      // freedom is along its run, which this view lays along screen-X.
      if (moveBayDrag(e.clientX)) return;

      // Check if we should start dragging (mouse moved beyond threshold)
      if (pendingDrag.current && !isDragging) {
        const dx = Math.abs(e.clientX - pendingDrag.current.startClientX);
        const dy = Math.abs(e.clientY - pendingDrag.current.startClientY);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
          // Start actual dragging
          setDraggedMeasurement(pendingDrag.current);
          setIsDragging(true);
        }
      }

      // Handle measurement line dragging (works for both line and label)
      if (isDragging && draggedMeasurement && svgRef.current) {
        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgPoint = pt.matrixTransform(svg.getScreenCTM().inverse());

        const { measurement, measurementKey, startX, startY, initialOffset } =
          draggedMeasurement;

        let newOffset;
        if (measurement.type === 'horizontal') {
          // Horizontal measurements can be dragged vertically
          const deltaY = svgPoint.y - startY;
          newOffset = initialOffset + deltaY;
          // Clamp the offset to reasonable bounds
          newOffset = Math.max(-200, Math.min(200, newOffset));
        } else if (measurement.type === 'vertical') {
          // Vertical measurements can be dragged horizontally
          const deltaX = svgPoint.x - startX;
          newOffset = initialOffset + deltaX;
          // Clamp the offset to reasonable bounds
          newOffset = Math.max(-200, Math.min(200, newOffset));
        }

        if (measurementKey) {
          setLocalOffsets((prev) => ({
            ...prev,
            [measurementKey]: newOffset,
          }));
        }
      }
    },
    [isDragging, draggedMeasurement, moveBayDrag]
  );

  const handleMouseUp = useCallback(
    (e) => {
      // A bay drag that never travelled is a click: it selects, and it must not
      // cost a run re-solve or a write.
      if (endBayDrag(e?.clientX)) return;

      // Clear pending drag (was a click, not a drag)
      pendingDrag.current = null;

      if (isDragging && draggedMeasurement) {
        const { measurementKey } = draggedMeasurement;
        const finalOffset = measurementOffsets[measurementKey];

        // Save the offset to Firebase
        if (
          onMeasurementOffsetSave &&
          measurementKey &&
          finalOffset !== undefined
        ) {
          onMeasurementOffsetSave(measurementKey, finalOffset);
        }

        setIsDragging(false);
        setDraggedMeasurement(null);
      }
    },
    [
      isDragging,
      draggedMeasurement,
      measurementOffsets,
      onMeasurementOffsetSave,
      endBayDrag,
    ]
  );

  // Add global mouse event listeners to detect drag threshold and handle dragging
  React.useEffect(() => {
    // Always listen for mouse events to detect drag threshold
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Calculate available space based on whether this is primary or preview
  const viewportWidth = window.innerWidth - 200; // Account for padding and margins
  const viewportHeight = isPrimary
    ? window.innerHeight - 500 // Leave space for larger preview strip at bottom
    : 180; // Larger height for preview tiles

  // Scale factor to fit walls nicely in the viewport
  let scale = 100; // Default 100 pixels per meter
  const padding = 20;

  // Calculate total width and max height for grouped walls
  let totalWidth = 0;
  let maxHeight = 0;
  const wallSegments = []; // Store individual wall segments with their positions

  if (isGrouped && groupedWalls) {
    // Calculate layout for grouped walls. Segments preserve their actual
    // physical gaps along the wall axis (in world coords) so objects
    // parented to one wall don't visually bleed into another wall when
    // there's empty space between them. Without gap preservation, a
    // stair tucked in a 4-foot gap between two wall segments would
    // overlap the adjacent wall's elevation. Gaps smaller than this
    // threshold get collapsed to zero so near-contiguous walls stitch
    // cleanly.
    const GAP_COLLAPSE_THRESHOLD = 0.1; // meters
    const referenceRight =
      wallData.groupReferenceRight || new THREE.Vector3(1, 0, 0);

    let currentX = 0;
    let prevRightEdgeWorld = null;

    groupedWalls.forEach((wall, idx) => {
      let segmentWidth, segmentHeight;
      let corners2D = null;

      // Check if this is a polygon wall
      if (isPolygonWall(wall)) {
        const bounds = getPolygonWallBounds(wall.polygonCorners);
        if (!bounds) {
          console.error(
            'RenderWallElevation: Failed to get bounds for polygon wall',
            wall.id
          );
          return;
        }
        segmentWidth = bounds.width;
        segmentHeight = bounds.height;
        corners2D = projectPolygonTo2D(wall.polygonCorners, 1, wall.rotation);
      } else if (
        wall.dimensions &&
        typeof wall.dimensions === 'object' &&
        !Array.isArray(wall.dimensions)
      ) {
        segmentWidth = wall.dimensions.x || 3;
        segmentHeight = wall.dimensions.y || 2.5;
      } else if (Array.isArray(wall.dimensions)) {
        segmentWidth = wall.dimensions[0] || 3;
        segmentHeight = wall.dimensions[1] || 2.5;
      } else {
        segmentWidth = 3;
        segmentHeight = 2.5;
      }

      // Compute the world-space extent of this wall along the group's
      // reference-right axis. Used to measure the gap to the previous
      // segment so we can preserve it in the rendered layout.
      const wallProjection = getWallPosition(wall).dot(referenceRight);
      const leftEdgeWorld = wallProjection - segmentWidth / 2;
      const rightEdgeWorld = wallProjection + segmentWidth / 2;

      if (prevRightEdgeWorld !== null) {
        const gap = leftEdgeWorld - prevRightEdgeWorld;
        if (gap > GAP_COLLAPSE_THRESHOLD) {
          currentX += gap;
          totalWidth += gap;
        }
      }

      wallSegments.push({
        wall,
        x: currentX,
        width: segmentWidth,
        height: segmentHeight,
        index: idx,
        corners2D: corners2D,
        isPolygon: isPolygonWall(wall),
      });

      currentX += segmentWidth;
      totalWidth += segmentWidth;
      maxHeight = Math.max(maxHeight, segmentHeight);
      prevRightEdgeWorld = rightEdgeWorld;
    });
  } else {
    // Single wall
    let width, height;
    let corners2D = null;

    // Check if this is a polygon wall
    if (isPolygonWall(wallData)) {
      const bounds = getPolygonWallBounds(wallData.polygonCorners);
      if (!bounds) {
        console.error(
          'RenderWallElevation: Failed to get bounds for polygon wall',
          wallData.id
        );
        return null;
      }
      width = bounds.width;
      height = bounds.height;
      corners2D = projectPolygonTo2D(
        wallData.polygonCorners,
        1,
        wallData.rotation
      );
    } else if (
      wallData.dimensions &&
      typeof wallData.dimensions === 'object' &&
      !Array.isArray(wallData.dimensions)
    ) {
      width = wallData.dimensions.x || 3;
      height = wallData.dimensions.y || 2.5;
    } else if (Array.isArray(wallData.dimensions)) {
      width = wallData.dimensions[0] || 3;
      height = wallData.dimensions[1] || 2.5;
    } else {
      width = 3;
      height = 2.5;
    }

    wallSegments.push({
      wall: wallData,
      x: 0,
      width,
      height,
      index: 0,
      corners2D: corners2D,
      isPolygon: isPolygonWall(wallData),
    });

    totalWidth = width;
    maxHeight = height;
  }

  // Adjust scale based on whether this is primary or preview
  if (isPrimary && previewScale !== null) {
    // Explicit scale provided (used by capture/PDF routes that need isPrimary for measurements)
    scale = previewScale;
  } else if (isPrimary) {
    // Calculate scale to fit within viewport (with some margin)
    const maxAvailableWidth = viewportWidth - 100; // Leave margin for rulers and padding
    const maxAvailableHeight = viewportHeight - 100; // Leave margin for rulers and padding

    const scaleByWidth = maxAvailableWidth / totalWidth;
    const scaleByHeight = maxAvailableHeight / maxHeight;

    // Use the smaller scale to ensure the wall fits both dimensions
    scale = Math.min(scaleByWidth, scaleByHeight, 200); // Cap at 200 pixels per meter max
    scale = Math.max(scale, 50); // Minimum 50 pixels per meter
  } else if (isPreview) {
    // For preview tiles, use provided scale for consistency, or calculate if not provided
    if (previewScale !== null) {
      scale = previewScale;
    } else {
      // Fallback: fit in 140px if no consistent scale provided
      const autoScale = 140 / Math.max(totalWidth, maxHeight);
      scale = Math.min(autoScale, 50);
    }
  }

  // The one number the bay drag needs: screen pixels per world metre, which is
  // this component's own `scale` times the host's CSS zoom. Published through
  // a ref because the drag handlers are registered on `document` and `scale`
  // is a local `let` derived above them.
  pxPerMetreRef.current = scale * (isPreview ? 1 : zoomScale || 1);

  // Capture mode = rendering for PDF/construction docs (thicker strokes for print)
  const isCapture = isPrimary && previewScale !== null;

  // Check if this wall is selected
  const wallId = wallData.id || `wall-${index}`;
  const isWallSelected = index === primaryWallIndex;

  const containerStyle = {
    width: totalWidth * scale,
    height: maxHeight * scale,
    position: 'relative',
    backgroundColor: isPreview || previewScale !== null ? '#ffffff' : '#fafafa', // White for previews/capture, light gray for main view
    margin: isPreview || previewScale !== null ? 0 : padding, // No margin for preview or capture mode
    cursor: 'pointer',
    borderRadius: 0, // Ensure walls have sharp corners, not rounded
    boxShadow: 'none', // No shadow for any walls
    transition: 'all 0.2s ease',
    opacity: isPreview && !isWallSelected ? 0.8 : 1,
  };

  const projectedObjects = nearbyObjects.map((rawObj) => {
    // Resolve room-copy openings against the canonical structure
    // counterpart so the elevation reads opening-itself fields
    // (doorType, wallFacesPriorityRoom) and coords from structure.
    // Non-opening nearby objects (and openings without an FK) pass
    // through unchanged.
    //
    // CRITICAL: `resolveRoomOpening` returns only structure-canonical
    // fields + a small set of room-side identity fields. Ad-hoc fields
    // that ElevationsViewer attaches when building nearbyObjects
    // (associatedWall, associatedWallIndex, roomId, distanceFromWall)
    // are dropped. Spreading rawObj over the resolved output preserves
    // them — without this, grouped walls fall back to wallData (the
    // combined wall whose properties come from the first sorted
    // segment) and openings project against the wrong segment's center.
    const obj =
      structureOpeningLookup && rawObj?.structureOpeningId
        ? {
            ...resolveRoomOpening(
              rawObj,
              structureOpeningLookup.get(rawObj.structureOpeningId)
            ),
            associatedWall: rawObj.associatedWall,
            associatedWallIndex: rawObj.associatedWallIndex,
            roomId: rawObj.roomId,
            distanceFromWall: rawObj.distanceFromWall,
          }
        : rawObj;
    // For grouped walls, use the associated wall for this object
    const wallForProjection = obj.associatedWall || wallData;
    const wallSegment =
      wallSegments.find((seg) => seg.wall === wallForProjection) ||
      wallSegments[0];

    const wallPos = getWallPosition(wallForProjection);
    const wallNormal = getWallNormal(wallForProjection);

    // For grouped walls, use the unified coordinate system from the group
    // This ensures all objects use the same reference frame
    let wallRight, wallUp;

    if (isGrouped && wallData.groupReferenceRight) {
      // Use the group's unified coordinate system
      wallRight = wallData.groupReferenceRight.clone();
      wallUp = new THREE.Vector3(0, 1, 0); // Always use world up for vertical
    } else {
      // One basis for the drawing AND the movement arrows — see
      // getWallRightVector. A polygon wall that lies flat keeps the
      // in-plane up it always had; every other wall stands on world up.
      wallRight = getWallRightVector(wallForProjection);
      wallUp =
        isPolygonWall(wallForProjection) && Math.abs(wallNormal.y) > 0.99
          ? new THREE.Vector3(0, 0, 1)
          : isPolygonWall(wallForProjection)
            ? new THREE.Vector3()
                .crossVectors(wallNormal, wallRight)
                .normalize()
            : new THREE.Vector3(0, 1, 0);
    }

    // Get object position and rotation
    let objPos;
    if (Array.isArray(obj.center)) {
      objPos = new THREE.Vector3(obj.center[0], obj.center[1], obj.center[2]);
    } else if (typeof obj.center === 'object') {
      objPos = new THREE.Vector3(
        obj.center.x || 0,
        obj.center.y || 0,
        obj.center.z || 0
      );
    } else {
      objPos = new THREE.Vector3(0, 0, 0);
    }

    // Get object rotation
    let objRotX = 0,
      objRotY = 0,
      objRotZ = 0;
    if (obj.rotation) {
      if (Array.isArray(obj.rotation)) {
        [objRotX, objRotY, objRotZ] = obj.rotation;
      } else if (typeof obj.rotation === 'object') {
        objRotX = obj.rotation.x || 0;
        objRotY = obj.rotation.y || 0;
        objRotZ = obj.rotation.z || 0;
      }
    }
    // Auto-detect if values are in degrees or radians
    const maxObjRot = Math.max(
      Math.abs(objRotX),
      Math.abs(objRotY),
      Math.abs(objRotZ)
    );
    if (maxObjRot > 2 * Math.PI) {
      objRotX = THREE.MathUtils.degToRad(objRotX);
      objRotY = THREE.MathUtils.degToRad(objRotY);
      objRotZ = THREE.MathUtils.degToRad(objRotZ);
    }
    const objRotation = new THREE.Euler(objRotX, objRotY, objRotZ);

    // Compute whether the door's own direction aligns with the SVG
    // horizontal axis (wallRight).  The door's local X-axis after rotation
    // gives its physical direction — the same vector the UV floor plan uses
    // to determine hinge placement.  When this direction opposes wallRight,
    // "hingeAtStart" (center − dir·halfWidth) maps to SVG right, not left.
    const doorDir = new THREE.Vector3(1, 0, 0).applyEuler(objRotation);
    // An L corner unit faces BOTH its walls: on its second leg the front
    // drawn is that leg's, which runs along the unit's local Z, so the face
    // split mirrors on that axis instead (`cornerLegOnWall`).
    const sideOnFrame = { wallNormal, wallRight, objRotation };
    const cornerLeg = cornerLegOnWall(obj, wallForProjection, sideOnFrame);
    const cornerFront = cornerLeg?.facing ? cornerLeg.leg : null;
    const doorDirAligned =
      cornerFront === 'B' ? cornerLeg.aligned : doorDir.dot(wallRight) > 0;

    // Calculate local coordinates on the wall
    const toObject = objPos.clone().sub(wallPos);

    const localX = toObject.dot(wallRight);
    // Heights from the DRAWN face's middle — what `segmentMidY` stands at —
    // not the stored box's centre, so an object sits on the same floor line
    // as the wall and its finishes (`drawnFaceMidAlongUp`).
    const localY =
      objPos.dot(wallUp) -
      drawnFaceMidAlongUp(wallForProjection, wallUp, wallPos);

    // Convert to 2D coordinates
    let x2D, y2D;

    // Each wall segment is drawn at `bottom: 0` with `height: segment.height`,
    // so its vertical midpoint in the container (measured from the top) is
    // `maxHeight - segment.height / 2`, NOT `maxHeight / 2`. The two differ
    // whenever a grouped wall has segments of differing heights — using
    // `maxHeight/2` then floats short-segment objects above the floor by
    // (maxHeight - segment.height) / 2 (e.g. cabinets hovering 0.5m up
    // when a 2.5m wall is grouped with a 3.5m tall wall).
    const segmentMidY = maxHeight - wallSegment.height / 2;

    if (wallSegment.isPolygon && wallSegment.corners2D) {
      // For polygon walls, we need to offset based on the polygon's bounds
      const minX = Math.min(...wallSegment.corners2D.map((c) => c.x));
      const maxX = Math.max(...wallSegment.corners2D.map((c) => c.x));
      const centerX = (minX + maxX) / 2;

      // COORDINATE MAPPING: Elevations View
      // Canvas Y = -3D Y (inverted)
      // The subtraction inverts Y: higher 3D Y → lower Canvas Y
      x2D = (wallSegment.x + localX + centerX) * scale;
      y2D = (segmentMidY - localY) * scale; // Invert Y for canvas
    } else {
      // Regular rectangular wall
      // COORDINATE MAPPING: Same inversion for rectangular walls
      x2D = (wallSegment.x + localX + wallSegment.width / 2) * scale;
      y2D = (segmentMidY - localY) * scale; // Invert Y: 3D up → Canvas down
    }

    // Calculate rotation angle for the projection
    const objForward = new THREE.Vector3(0, 0, -1).applyEuler(objRotation);
    const projectedForward = objForward
      .clone()
      .sub(wallNormal.clone().multiplyScalar(objForward.dot(wallNormal)));
    const angle =
      (Math.atan2(
        projectedForward.dot(wallRight),
        -projectedForward.dot(wallUp)
      ) *
        180) /
      Math.PI;

    // Get object depth to calculate actual distance from wall
    // Try GLB first, then fall back to obj.dimensions
    let objDepth = 0;
    const depthTypeKey =
      typeof obj.type === 'object' && obj.type
        ? Object.keys(obj.type)[0]
        : obj.type || obj.category;
    const depthObjProjections = objectProjections?.get(obj.id);

    // First try GLB lookup by projection URL matching
    if (
      glbObjects &&
      depthTypeKey &&
      glbObjects[depthTypeKey] &&
      depthObjProjections
    ) {
      const glbModel = glbObjects[depthTypeKey].find((g) => {
        if (!g.edgeProjections) return false;
        return Object.values(g.edgeProjections).some((ep) =>
          Object.values(depthObjProjections).some((p) => p.url === ep.url)
        );
      });
      if (glbModel?.dimensions) {
        const sf =
          glbModel.dimensions.unit === 'inches' ? 1 : glbModel.scaleFactor || 1;
        objDepth =
          (glbModel.dimensions.depth || glbModel.dimensions[2] || 0) *
          sf *
          0.0254;
      } else if (glbModel?.originalDimensions) {
        const sf = glbModel.scaleFactor || 1;
        objDepth = glbModel.originalDimensions.depth * sf * 0.0254;
      }
    }

    // Fall back to obj.dimensions if no GLB depth found
    if (objDepth === 0 && obj.dimensions) {
      if (
        typeof obj.dimensions === 'object' &&
        !Array.isArray(obj.dimensions)
      ) {
        objDepth = obj.dimensions.depth || obj.dimensions.z || 0;
        // Convert to meters if needed
        if (obj.dimensions.unit === 'inches' || objDepth > 10) {
          objDepth *= 0.0254;
        }
      } else if (Array.isArray(obj.dimensions)) {
        objDepth = obj.dimensions[2] || 0;
      }
    }

    // Calculate actual distance from wall surface to object surface
    const distanceFromCenter = Math.abs(toObject.dot(wallNormal));
    const distanceFromWall = Math.max(0, distanceFromCenter - objDepth / 2);

    // Calculate actual rendered width and height for bounding box calculation
    let renderedWidth = 0;
    let renderedHeight = 0;

    // Get dimensions - prioritize GLB model dimensions over room data dimensions
    let width = 0,
      height = 0,
      depth = 0;
    let dimensionsUnit = 'meters'; // Track unit for conversion
    let glbRotation = null; // Track GLB rotation for dimension transformation

    // Get type key for GLB lookup
    const typeKey =
      typeof obj.type === 'object' && obj.type
        ? Object.keys(obj.type)[0]
        : obj.type || obj.category;

    // Get object's projections for matching
    const objProjections = objectProjections?.get(obj.id);

    // First, try to find GLB model by projection URL matching (most reliable)
    if (glbObjects && typeKey && glbObjects[typeKey] && objProjections) {
      const glbModel = glbObjects[typeKey].find((g) => {
        if (!g.edgeProjections) return false;
        // Check if any projection URL matches
        return Object.values(g.edgeProjections).some((ep) =>
          Object.values(objProjections).some((p) => p.url === ep.url)
        );
      });

      if (glbModel) {
        // Get rotation if available
        if (glbModel.rotation) {
          glbRotation = glbModel.rotation;
        }

        // Get dimensions from GLB model
        // If dimensions.unit is 'inches', they're already in inches - just convert to meters
        // Otherwise apply scaleFactor to convert native GLB units to inches first
        if (glbModel.dimensions) {
          const sf =
            glbModel.dimensions.unit === 'inches'
              ? 1
              : glbModel.scaleFactor || 1;
          width =
            (glbModel.dimensions.width || glbModel.dimensions[0] || 0) *
            sf *
            0.0254;
          height =
            (glbModel.dimensions.height || glbModel.dimensions[1] || 0) *
            sf *
            0.0254;
          depth =
            (glbModel.dimensions.depth || glbModel.dimensions[2] || 0) *
            sf *
            0.0254;
          dimensionsUnit = 'meters'; // Already converted
        } else if (glbModel.originalDimensions) {
          const sf = glbModel.scaleFactor || 1;
          width = glbModel.originalDimensions.width * sf * 0.0254;
          height = glbModel.originalDimensions.height * sf * 0.0254;
          depth = glbModel.originalDimensions.depth * sf * 0.0254;
          dimensionsUnit = 'meters'; // Already converted
        }
      }
    }

    // Second, try to find by modelId if projection matching didn't work
    if (
      width === 0 &&
      height === 0 &&
      depth === 0 &&
      obj.modelId &&
      glbObjects &&
      typeKey &&
      glbObjects[typeKey]
    ) {
      const cleanModelId = obj.modelId.startsWith('saved_')
        ? obj.modelId.substring(6)
        : obj.modelId;

      const glbModel = glbObjects[typeKey].find(
        (m) =>
          m.id === obj.modelId ||
          m.id === cleanModelId ||
          m.url === obj.modelUrl ||
          m.glbURL === obj.modelUrl
      );

      if (glbModel) {
        if (glbModel.rotation) {
          glbRotation = glbModel.rotation;
        }

        if (glbModel.dimensions) {
          const sf2 =
            glbModel.dimensions.unit === 'inches'
              ? 1
              : glbModel.scaleFactor || 1;
          width =
            (glbModel.dimensions.width || glbModel.dimensions.x || 0) * sf2;
          height =
            (glbModel.dimensions.height || glbModel.dimensions.y || 0) * sf2;
          depth =
            (glbModel.dimensions.depth || glbModel.dimensions.z || 0) * sf2;
          dimensionsUnit = glbModel.dimensions.unit || 'inches';
        }
      }
    }

    // Fall back to obj.dimensions if no GLB dimensions found
    if (width === 0 && height === 0 && depth === 0 && obj.dimensions) {
      if (
        typeof obj.dimensions === 'object' &&
        !Array.isArray(obj.dimensions)
      ) {
        width = obj.dimensions.x || obj.dimensions.width || 0;
        height = obj.dimensions.y || obj.dimensions.height || 0;
        depth = obj.dimensions.z || obj.dimensions.depth || 0;
        dimensionsUnit = obj.dimensions.unit || 'meters';
      } else if (Array.isArray(obj.dimensions)) {
        [width, height, depth] = obj.dimensions;
        width = width || 0;
        height = height || 0;
        depth = depth || 0;
        dimensionsUnit = 'meters'; // Array dimensions are typically in meters
      }
    }

    // Convert inches to meters if needed
    if (dimensionsUnit === 'inches') {
      const INCHES_TO_METERS = 0.0254;
      width *= INCHES_TO_METERS;
      height *= INCHES_TO_METERS;
      depth *= INCHES_TO_METERS;
    }

    if (width > 0 || height > 0 || depth > 0) {
      // Note: Do NOT apply applyRotationToDimensions here.
      // All dimension sources (GLB model dimensions and obj.dimensions from design tools)
      // store post-rotation visual dimensions (bounding box computed after rotation).
      // Applying GLB rotation would cause double-rotation and incorrect sizing.
      let rotatedDims = { width, height, depth };

      // Determine which view we're looking at to select correct dimension
      const viewName = getProjectionView(wallNormal, objRotation);

      // Select appropriate dimension based on view (matching render logic)
      if (viewName === 'left' || viewName === 'right') {
        renderedWidth = rotatedDims.depth * scale;
      } else {
        renderedWidth = rotatedDims.width * scale;
      }

      // Height is always the Y dimension for elevation views
      renderedHeight = rotatedDims.height * scale;
    }

    return {
      ...obj,
      x2D,
      y2D,
      // The ceiling line for THIS object, in container px from the top:
      // each segment is drawn at `bottom: 0`, so its top sits
      // `maxHeight - segment.height` above the floor line. Grouped walls
      // of differing heights each get their own. Everything the object
      // draws above it is clipped — see `ceilingClipPath`.
      ceilingY2D: (maxHeight - (wallSegment?.height ?? maxHeight)) * scale,
      distanceFromWall,
      rotation2D: angle,
      renderedWidth,
      renderedHeight,
      doorDirAligned,
      // which face of it this elevation sees ('front' — its +Z face)
      projectionView: getProjectionView(wallNormal, objRotation),
      cornerFront,
      // What of it is casework turned across the line of sight — a
      // peninsula, a run on a perpendicular wall, the other leg of a corner
      // unit — asked in the frame this pass projects with.
      sideOnSpan: sideOnShadeSpan(obj, wallForProjection, sideOnFrame),
    };
  });

  // The shade under each side-on object: its span of the box it is drawn in
  // (`x2D ± renderedWidth / 2`, as the run handles and labels read it), full
  // height, cut at the same ceiling line as the object itself.
  const sideOnShades = projectedObjects
    .filter((o) => o.sideOnSpan && o.renderedWidth > 0 && o.renderedHeight > 0)
    .map((o) => {
      const boxLeft = o.x2D - o.renderedWidth / 2;
      const top = o.y2D - o.renderedHeight / 2;
      return {
        id: o.id,
        left: boxLeft + o.sideOnSpan.from * o.renderedWidth,
        top,
        width: (o.sideOnSpan.to - o.sideOnSpan.from) * o.renderedWidth,
        height: o.renderedHeight,
        clipPath: ceilingClipPath(o.ceilingY2D, top),
      };
    });

  // Cabinet trim (`utils/cabinetTrimElevation`): a crown / stacking band on
  // top of each cabinet that carries one, a light-rail band under it, at the
  // heights the shared pieces give — over the part of the box drawn front-on.
  const trimBandsByCarrier = cabinetTrimBandsByCarrier(
    wallData?.cabinetTrimPieces
  );
  // each cabinet's front-on box, in container px, and a world height's px
  const frontOnBoxes = new Map();
  projectedObjects.forEach((o) => {
    if (!(o.renderedWidth > 0) || !(o.renderedHeight > 0)) return;
    const front = frontOnSpan(o.sideOnSpan);
    if (!front) return;
    const boxTop = o.y2D - o.renderedHeight / 2;
    const worldTop = (o.center?.y ?? 0) + (o.dimensions?.y ?? 0) / 2;
    frontOnBoxes.set(o.id, {
      left: o.x2D - o.renderedWidth / 2 + front.from * o.renderedWidth,
      width: (front.to - front.from) * o.renderedWidth,
      // world height → container px, from the box's own top
      pxAt: (y) => boxTop - (y - worldTop) * scale,
      view: o.projectionView,
      aligned: o.doorDirAligned,
    });
  });
  const trimBands = [...frontOnBoxes.entries()].flatMap(([id, box]) => {
    const bands = trimBandsByCarrier.get(id);
    if (!bands) return [];
    return Object.entries(bands).map(([kind, b]) => ({
      key: `${id}-${kind}`,
      kind,
      left: box.left,
      width: box.width,
      top: box.pxAt(b.topY),
      height: (b.topY - b.bottomY) * scale,
    }));
  });
  // the valance over a sink, between the two uppers it hangs between, the
  // crown carried across it
  const valanceBoxes = valanceElevationBoxes(
    wallData?.cabinetTrimPieces,
    (id) => frontOnBoxes.get(id) || null,
    trimBandsByCarrier,
    scale
  );
  // corbels and posts on a seated island, on the elevation facing its back
  const countedPieceBoxes = countedPieceElevationBoxes(
    wallData?.cabinetTrimPieces,
    (id) => frontOnBoxes.get(id) || null,
    scale
  );

  // Walls that return into the room off this one, drawn END-ON: a band the
  // thickness of the returning wall, standing on the floor line. Each band
  // arrives as two WORLD points on its wall's line (`endOnWallBands`) and is
  // projected with the SAME basis the objects above use — the group's when
  // this is a grouped wall, the wall's own otherwise — so a member wall whose
  // right vector opposes the group's cannot mirror its band.
  const endOnBands = [];
  const endOnSeen = new Set();
  wallSegments.forEach((segment) => {
    const bands = segment.wall?.endOnBands;
    if (!bands?.length) return;
    const right =
      isGrouped && wallData.groupReferenceRight
        ? wallData.groupReferenceRight
        : getWallRightVector(segment.wall);
    const origin = getWallPosition(segment.wall);
    const along = (p) =>
      (p.x - origin.x) * right.x + (p.z - origin.z) * right.z;
    bands.forEach((band) => {
      // A return between two collinear segments is claimed by BOTH, and in a
      // grouped elevation both segments are drawn — key it by the world place
      // it stands in, so it is drawn once.
      const key = [band.a.x, band.a.z, band.b.x, band.b.z]
        .map((v) => v.toFixed(3))
        .join(',');
      if (endOnSeen.has(key)) return;
      endOnSeen.add(key);
      const s0 = along(band.a);
      const s1 = along(band.b);
      const left = segment.x + Math.min(s0, s1) + segment.width / 2;
      const width = Math.abs(s1 - s0);
      const height = Math.min(band.height, maxHeight);
      endOnBands.push({
        key,
        left: left * scale,
        width: width * scale,
        height: height * scale,
        // The drawing's floor line is the bottom of the container.
        top: (maxHeight - height) * scale,
      });
    });
  });

  // Finishes laid ON each segment's wall — a backsplash band, shower or tub
  // tile, a hand-drawn tile or paint section (item #1). Each arrives as two
  // WORLD plan points in the frame the 3D view tiles it in
  // (`wallFinishSpans`) and is projected with the SAME basis as the objects
  // and the end-on bands, so the drawing cannot mirror what the 3D shows.
  const wallFinishBands = [];
  const finishLabelPx = wallFinishLabelPx(measurementFontSize);
  // The doors and windows drawn here, resolved: each finish is cut at its own
  // wall's by the hole the 3D carves (`wallFinishSpans`).
  const finishOpenings = projectedObjects.filter((o) => openingKindOf(o));
  wallSegments.forEach((segment) => {
    const preview = previewWallRegions?.get(segment.wall?.id);
    const drawnWall = preview
      ? {
          ...segment.wall,
          texture: { ...(segment.wall.texture || {}), regions: preview },
        }
      : segment.wall;
    const spans = wallFinishSpans(drawnWall, {
      cardKind: bandTargets?.get(segment.wall?.id)?.kind ?? null,
      openings: finishOpenings,
      // what stands in front of it, cut by the take-off's own rule
      objects: projectedObjects,
    });
    // How a world height lands on this wall's drawing — what a dragged top
    // edge and its snap lines are placed by (item #28).
    const frame = bandHeightFrame(segment.wall);
    if (!spans.length) return;
    const right =
      isGrouped && wallData.groupReferenceRight
        ? wallData.groupReferenceRight
        : getWallRightVector(segment.wall);
    const origin = getWallPosition(segment.wall);
    spans.forEach((span) => {
      const placed = placeWallFinish(span, {
        segmentX: segment.x,
        segmentWidth: segment.width,
        origin,
        right,
        maxHeight,
        scale,
      });
      if (!(placed.width > 0) || !(placed.height > 0)) return;
      wallFinishBands.push({
        ...span,
        ...placed,
        // the top in drawing metres (`placed.top` is px)
        topM: Math.min(span.top, maxHeight),
        frame,
        key: `${segment.wall?.id || segment.index}-${span.key}`,
        // The band's name is a LABEL: it follows the elevation's labels
        // toggle (`showLabels`) like the object labels do, and a label that
        // would crowd its band is left off (the band's `title` still names
        // it); never on a preview tile. The band itself always draws.
        showLabel:
          showLabels &&
          !isPreview &&
          placed.width >= finishLabelPx * 6 &&
          placed.height >= finishLabelPx * 1.4,
      });
    });
  });

  // Backsplash bands are edited on the primary wall of an editing host only
  // (item #28) — never a preview tile or a capture. The windows' boxes tell
  // a band piece cut at the glass, whose edge no setting moves.
  const liveBandInteraction =
    isPrimary && !isPreview && !isCapture ? bandInteraction : null;
  const bandOpeningRects = liveBandInteraction
    ? projectedObjects
        .filter((o) => o.type === 'window' || o.category?.window)
        .filter((o) => o.renderedWidth > 0 && o.renderedHeight > 0)
        .map((o) => ({
          left: o.x2D - o.renderedWidth / 2,
          top: o.y2D - o.renderedHeight / 2,
          width: o.renderedWidth,
          height: o.renderedHeight,
        }))
    : [];

  // Published for the click resolver, which is defined above this (it has to
  // be — the handlers are bound to the object divs) and needs to look an id
  // up in what is actually drawn.
  projectedRef.current = projectedObjects;

  // The MOVE handle belongs to the SELECTION: drawn for the selected object
  // whenever that object can travel along its run — for a mouse and a finger
  // alike, with no "Move along run" in front of it (user request 2026-10-26:
  // "once you click a cabinet, a blue move button can appear… if you drag
  // that, it's unmistakable"). Pressing it is the drag; the object's body
  // still pans unless the mode is on.
  const bayGrabHandle = (() => {
    if (isPreview) return null;
    // In RUN scope the run is the edit target and its own handles stand on
    // the line; the box clicked to get there is not what moves, so it shows
    // no move handle — the plan's rule (`moveHandleOfferedFor`). It used to
    // stay under that box (user report 2026-10-26).
    if (runHandleModel?.scope === 'run') return null;
    const selectedId =
      previewSelectedId || selectedObjectId || selectedObject || null;
    const target = selectedId
      ? projectedObjects.find((o) => o?.id === selectedId)
      : null;
    if (!target || !canMoveAlongRun(target)) return null;
    const drawnHeightPx = target.renderedHeight || 0;
    const g = elevationHandleGeometry(
      { x2D: target.x2D, y2D: target.y2D, drawnHeightPx },
      zoomScale || 1
    );
    return g
      ? { ...g, object: target, tetherPx: drawnHeightPx / 2 + g.radius }
      : null;
  })();

  // Calculate content bounding box for preview spacing
  // Wall bounds: 0 to totalWidth * scale
  const wallWidthPx = totalWidth * scale;

  // The RUN's handles for the selected object. Never in a preview, a PDF
  // capture or a construction-doc page — this primitive has eight call sites
  // and most of them are drawings rather than editors, which is what the
  // `isPreview` gate already separates.
  const runHandles = (() => {
    if (isPreview || !runHandleModel) return [];
    const boxId = previewSelectedId || selectedIdRef.current;
    const box = boxId ? projectedObjects.find((o) => o?.id === boxId) : null;
    if (!box) return [];
    const zoom = zoomScale || 1;
    if (runHandleModel.scope === 'bay') {
      return bayWidthHandles(
        { x2D: box.x2D, y2D: box.y2D, renderedWidth: box.renderedWidth },
        zoom,
        { pinned: runHandleModel.bayPinned, reason: runHandleModel.bayReason }
      );
    }
    // RUN scope: the ends and the leg disc, placed by param. The band sits on
    // the selected box's own line so every run handle shares one row.
    const xForParam = paramToElevationX(
      { x2D: box.x2D },
      runHandleModel.refParam,
      runHandleModel.sign,
      scale
    );
    if (!xForParam) return [];
    const band = { y: box.y2D, minX: 0, maxX: wallWidthPx };
    const leg = legMoveHandle(
      runHandleModel.legMidParam,
      xForParam,
      band,
      zoom
    );
    return [
      ...runEndHandles(runHandleModel.ends || [], xForParam, band, zoom),
      ...(leg ? [leg] : []),
    ];
  })();
  let contentMinX = 0;
  let contentMaxX = wallWidthPx;

  if (isPreview) {
    projectedObjects.forEach((obj) => {
      const halfWidth = obj.renderedWidth / 2;
      const objLeft = obj.x2D - halfWidth;
      const objRight = obj.x2D + halfWidth;
      contentMinX = Math.min(contentMinX, objLeft);
      contentMaxX = Math.max(contentMaxX, objRight);
    });
  }

  // Calculate overflow on each side
  const leftOverflow = Math.max(0, -contentMinX);
  const rightOverflow = Math.max(0, contentMaxX - wallWidthPx);

  // Record render stats for performance monitoring (only for primary wall)
  // Called directly during render since it only updates a global monitoring object
  if (isPrimary && projectedObjects.length > 0) {
    let svgProjectionCount = 0;
    let fallbackCount = 0;

    projectedObjects.forEach((obj) => {
      const objProjections = objectProjections?.get(obj.id);

      // Check if this object would render with SVG projection
      let hasProjection = false;
      if (obj.modelId) {
        const cleanModelId = obj.modelId.startsWith('saved_')
          ? obj.modelId.substring(6)
          : obj.modelId;
        hasProjection =
          objectProjections?.has(obj.modelId) ||
          objectProjections?.has(cleanModelId);
      }
      if (!hasProjection && objProjections) {
        hasProjection = true;
      }

      if (hasProjection) {
        svgProjectionCount++;
      } else {
        fallbackCount++;
      }
    });

    recordRenderStats({
      objectCount: projectedObjects.length,
      svgProjectionCount,
      fallbackCount,
    });
  }

  return (
    <div
      key={wallData.id || index}
      style={{
        display: 'inline-block',
        verticalAlign: 'top',
        position: 'relative',
        // Add padding for overflow so gap between containers = gap between content
        paddingLeft: isPreview ? leftOverflow : 0,
        paddingRight: isPreview ? rightOverflow : 0,
      }}
    >
      <div
        style={containerStyle}
        onMouseDown={handleObjectMouseDown}
        onClick={(e) => {
          // Clicked outside objects/openings - deselect (only on true click, not drag)
          if (!isPanning && isClickNotDrag(e)) {
            setSelectedObject(null);
            setSelectedObjectData(null);
            if (onObjectSelect) {
              onObjectSelect(null);
            }
          }
        }}
      >
        {/* Render wall segments with dividers for grouped walls */}
        {isGrouped &&
          wallSegments.map((segment, idx) => (
            <React.Fragment key={idx}>
              {/* Draw divider line between segments */}
              {idx > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: segment.x * scale - 1,
                    top: 0,
                    width: 2,
                    height: maxHeight * scale,
                    backgroundColor: '#999',
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }}
                />
              )}
              {/* Draw individual wall backgrounds to show height differences */}
              {segment.isPolygon &&
              segment.corners2D &&
              segment.corners2D.length > 0 ? (
                // Render polygon wall shape
                <svg
                  style={{
                    position: 'absolute',
                    left: segment.x * scale,
                    bottom: 0,
                    width: Math.max(segment.width * scale, 10),
                    height: Math.max(segment.height * scale, 10),
                    pointerEvents: 'none',
                  }}
                >
                  <path
                    d={
                      segment.corners2D
                        .map((corner, i) => {
                          const x = corner.x * scale;
                          const y = segment.height * scale - corner.y * scale;
                          return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                        })
                        .join(' ') + ' Z'
                    }
                    fill="#ffffff"
                    stroke="none"
                  />
                </svg>
              ) : (
                // Regular rectangular wall
                <div
                  style={{
                    position: 'absolute',
                    left: segment.x * scale,
                    bottom: 0,
                    width: segment.width * scale,
                    height: segment.height * scale,
                    backgroundColor: '#ffffff',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </React.Fragment>
          ))}

        {/* Draw wall boundary outline */}
        {wallSegments.some((seg) => seg.isPolygon) ? (
          // For polygon walls or mixed walls, draw each segment's outline
          wallSegments.map((segment, idx) => (
            <React.Fragment key={`outline-${idx}`}>
              {segment.isPolygon &&
              segment.corners2D &&
              segment.corners2D.length > 0 ? (
                <svg
                  style={{
                    position: 'absolute',
                    left: segment.x * scale,
                    bottom: 0,
                    width: Math.max(segment.width * scale, 10),
                    height: Math.max(segment.height * scale, 10),
                    pointerEvents: 'none',
                  }}
                >
                  <path
                    d={
                      segment.corners2D
                        .map((corner, i) => {
                          const x = corner.x * scale;
                          const y = segment.height * scale - corner.y * scale;
                          return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                        })
                        .join(' ') + ' Z'
                    }
                    fill="none"
                    stroke={isPreview && isWallSelected ? '#3b82f6' : '#333'}
                    strokeWidth={
                      isPreview && isWallSelected
                        ? 3
                        : isCapture
                          ? CAPTURE_STROKE_PX.wall
                          : 0.75
                    }
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              ) : (
                <svg
                  style={{
                    position: 'absolute',
                    left: segment.x * scale,
                    bottom: 0,
                    width: segment.width * scale,
                    height: segment.height * scale,
                    overflow: 'visible',
                    pointerEvents: 'none',
                  }}
                >
                  <rect
                    x={0}
                    y={0}
                    width={segment.width * scale}
                    height={segment.height * scale}
                    fill="none"
                    stroke={isPreview && isWallSelected ? '#3b82f6' : '#333'}
                    strokeWidth={
                      isPreview && isWallSelected
                        ? 3
                        : isCapture
                          ? CAPTURE_STROKE_PX.wall
                          : 0.75
                    }
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
            </React.Fragment>
          ))
        ) : (
          // All rectangular walls - use single SVG outline
          <svg
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: totalWidth * scale,
              height: maxHeight * scale,
              overflow: 'visible',
              pointerEvents: 'none',
            }}
          >
            {/* A free-standing leg's face (island / peninsula Side A / B) is
                drawn in the same full-height frame as the room's walls, so it
                shares their scale — but it is NOT backed by a wall, and a
                rectangle around it reads as one. It gets the floor line only,
                which is the whole of what stands behind an island. */}
            {wallData.isFreestandingElevation ? (
              <line
                x1={0}
                y1={maxHeight * scale}
                x2={totalWidth * scale}
                y2={maxHeight * scale}
                stroke={isPreview && isWallSelected ? '#3b82f6' : '#333'}
                strokeWidth={
                  isPreview && isWallSelected
                    ? 3
                    : isCapture
                      ? CAPTURE_STROKE_PX.wall
                      : 0.75
                }
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <rect
                x={0}
                y={0}
                width={totalWidth * scale}
                height={maxHeight * scale}
                fill="none"
                stroke={isPreview && isWallSelected ? '#3b82f6' : '#333'}
                strokeWidth={
                  isPreview && isWallSelected
                    ? 3
                    : isCapture
                      ? CAPTURE_STROKE_PX.wall
                      : 0.75
                }
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
        )}

        {/* Finishes laid on the wall face — backsplash, shower or tub tile,
            tile and paint sections (item #1). The lowest layer: drawn over the
            wall rectangle and UNDER the end-on bands and every object, so a
            cabinet standing in front stays clickable. Only a Backsplash-card
            band or a tile section on the primary wall of an editing host
            takes the pointer (item #28), and only where nothing stands in
            front of it. */}
        <WallFinishBandLayer
          bands={wallFinishBands}
          isCapture={isCapture}
          finishLabelPx={finishLabelPx}
          interaction={liveBandInteraction}
          isClickNotDrag={isClickNotDrag}
          isPanning={isPanning}
          openingRects={bandOpeningRects}
          pxPerMetre={scale * (zoomScale || 1)}
          coarsePointer={coarsePointer}
          zoom={zoomScale || 1}
          scale={scale}
        />

        {/* A wall returning into the room, seen END-ON: the elevation shows
            its thickness as a band standing on the floor. Drawn over the wall
            face and UNDER the objects — an object drawn on this wall stays
            clickable, and the band is a plan fact about the wall, not a thing
            in front of the drawing. */}
        {endOnBands.map((band) => (
          <div
            key={`end-on-${band.key}`}
            data-elevation-end-on-band
            style={{
              position: 'absolute',
              left: band.left,
              top: band.top,
              width: band.width,
              height: band.height,
              backgroundColor: END_ON_BAND_FILL,
              borderLeft: `${isCapture ? CAPTURE_STROKE_PX.object : 0.75}px solid ${END_ON_BAND_STROKE}`,
              borderRight: `${isCapture ? CAPTURE_STROKE_PX.object : 0.75}px solid ${END_ON_BAND_STROKE}`,
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Casework seen SIDE-ON — a peninsula, a run on a perpendicular wall
            reaching toward the viewer — shaded with the end-on wall's poché.
            A layer of its own, over the wall's finishes and UNDER every
            object: it hides the finish behind the cabinet, but no object's
            outline, and is never hit-tested. */}
        {sideOnShades.map((shade) => (
          <div
            key={`side-on-${shade.id}`}
            data-elevation-side-on-shade={shade.id}
            style={{
              position: 'absolute',
              left: shade.left,
              top: shade.top,
              width: shade.width,
              height: shade.height,
              ...SIDE_ON_SHADE_BACKGROUND,
              clipPath: shade.clipPath,
              pointerEvents: 'none',
            }}
          />
        ))}

        {trimBands.map((band) => (
          <div
            key={`cabinet-trim-${band.key}`}
            data-elevation-cabinet-trim={band.kind}
            style={{
              position: 'absolute',
              left: band.left,
              top: band.top,
              width: band.width,
              height: band.height,
              ...CABINET_TRIM_BAND_STYLE,
              pointerEvents: 'none',
            }}
          />
        ))}

        {countedPieceBoxes.map((p) => (
          <div
            key={`counted-piece-${p.key}`}
            data-elevation-counted-piece={p.kind}
            style={{
              position: 'absolute',
              left: p.left,
              top: p.top,
              width: p.width,
              height: p.height,
              ...CABINET_TRIM_BAND_STYLE,
              // a round post reads round: shaded at its edges
              ...(p.shape === 'round'
                ? {
                    background:
                      'linear-gradient(90deg, rgba(60,60,60,0.18), rgba(60,60,60,0.04) 35%, rgba(60,60,60,0.04) 65%, rgba(60,60,60,0.18))',
                  }
                : {}),
              pointerEvents: 'none',
            }}
          />
        ))}

        {valanceBoxes.map((v) => (
          <React.Fragment key={`cabinet-valance-${v.key}`}>
            <svg
              data-elevation-cabinet-valance={v.style}
              style={{
                position: 'absolute',
                left: v.left,
                top: v.top,
                width: v.width,
                height: v.height,
                overflow: 'visible',
                pointerEvents: 'none',
              }}
            >
              {[v.outline, ...v.panels].map((pts, i) => (
                <polygon
                  key={i}
                  points={pts.map(([x, y]) => `${x},${y}`).join(' ')}
                  fill={i === 0 ? CABINET_TRIM_BAND_STYLE.background : 'none'}
                  stroke="rgba(60, 60, 60, 0.85)"
                  strokeWidth={1}
                />
              ))}
            </svg>
            {v.bands.map((band) => (
              <div
                key={band.kind}
                data-elevation-cabinet-trim={band.kind}
                style={{
                  position: 'absolute',
                  left: v.left,
                  top: band.top,
                  width: v.width,
                  height: band.height,
                  ...CABINET_TRIM_BAND_STYLE,
                  pointerEvents: 'none',
                }}
              />
            ))}
          </React.Fragment>
        ))}

        {/* Render objects */}
        {/* The MOVE handle. A disc BELOW the object it moves, tethered
            to it, drawn once as an overlay rather than inside either object
            branch — it belongs to the selection, not to a render path, and
            neither branch's own styles should reach it. Same sizes as the
            plan's handle (`elevationGrabHandle` re-exports its constants), so
            one gesture does not grow two different buttons. */}
        {/* The RUN's handles: a bay's two width edges in BAY scope, the run's
            ends and a free leg's disc in RUN scope. Each is its own positioned
            element, so the browser hit-tests them — which is why the reaches
            are built not to overlap (`elevationRunHandles`), rather than a
            nearest-wins test in code as the plan needs.

            A HANDLE needs no mode in front of it: grabbing one is unambiguous,
            and only a drag on an object's BODY could equally mean pan or
            select. Same rule as the floor plan. */}
        {/* The picked backsplash band's grip, handle, side hints, snap lines
            and readout (item #28) — over the objects, like the handles. */}
        <BandGripLayer
          bands={wallFinishBands}
          interaction={liveBandInteraction}
          openingRects={bandOpeningRects}
          pxPerMetre={scale * (zoomScale || 1)}
          scale={scale}
          maxHeight={maxHeight}
          zoom={zoomScale || 1}
          coarsePointer={coarsePointer}
        />

        {runHandles.map((h) => (
          <svg
            key={h.key}
            data-elevation-run-handle={h.key}
            width={h.hit.left + h.hit.right}
            height={h.hitRadius * 2}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => handleRunHandleTouchStart(e, h)}
            onMouseDown={(e) => handleRunHandleMouseDown(e, h)}
            style={{
              position: 'absolute',
              left: h.x - h.hit.left,
              top: h.y - h.hitRadius,
              overflow: 'visible',
              touchAction: 'none',
              cursor: h.pinned ? 'not-allowed' : 'ew-resize',
              zIndex: 21,
            }}
          >
            <circle
              cx={h.hit.left}
              cy={h.hitRadius}
              r={h.radius}
              fill={h.pinned ? '#9ca3af' : '#0066ff'}
              stroke="#ffffff"
              strokeWidth={2 / (zoomScale || 1)}
            />
            {/* The one axis anything here can travel, as on the plan's own
                end handles. */}
            <path
              d={arrowPath(
                h.hit.left,
                h.hitRadius,
                h.radius * 0.55,
                h.radius * 0.22
              )}
              stroke="#ffffff"
              strokeWidth={2 / (zoomScale || 1)}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        ))}

        {/* A PIN on each cabinet the user fixed — what a dragged neighbour
            stops against (`fixedCabinetMarker`). Top-right, inside the box;
            the editing view only, never a preview tile or a capture. A click
            on it releases the cabinet; it stops the press first, so the click
            neither arms a drag nor pans, nor reaches the cabinet under it. */}
        {!isPreview &&
          !isCapture &&
          projectedObjects
            .filter(
              (o) =>
                isFixedCabinet(o) &&
                Number.isFinite(o?.x2D) &&
                Number.isFinite(o?.y2D) &&
                o.renderedWidth > 0 &&
                o.renderedHeight > 0
            )
            .map((o) => {
              const size = FIXED_MARKER_PX / (zoomScale || 1);
              const inset = size * 0.15;
              const box = FIXED_MARKER.box;
              const live = !!onUnpinCabinet;
              const stop = (e) => e.stopPropagation();
              return (
                <svg
                  key={`fixed-pin-${o.id}`}
                  data-elevation-fixed-pin={o.id}
                  width={size}
                  height={size}
                  viewBox={`0 0 ${box} ${box}`}
                  onPointerDown={live ? stop : undefined}
                  onMouseDown={live ? stop : undefined}
                  onTouchStart={live ? stop : undefined}
                  onClick={
                    live
                      ? (e) => {
                          e.stopPropagation();
                          onUnpinCabinet(o);
                        }
                      : undefined
                  }
                  style={{
                    position: 'absolute',
                    left: o.x2D + o.renderedWidth / 2 - size - inset,
                    top: o.y2D - o.renderedHeight / 2 + inset,
                    overflow: 'visible',
                    pointerEvents: live ? 'auto' : 'none',
                    cursor: live ? 'pointer' : undefined,
                    zIndex: 19,
                  }}
                >
                  {live ? (
                    <title>
                      Unpin — let the run size and place this cabinet
                    </title>
                  ) : null}
                  <g
                    transform={`rotate(${FIXED_MARKER.tiltDeg} ${box / 2} ${box / 2})`}
                  >
                    <line
                      x1={FIXED_MARKER.needle.x1}
                      y1={FIXED_MARKER.needle.y1}
                      x2={FIXED_MARKER.needle.x2}
                      y2={FIXED_MARKER.needle.y2}
                      stroke={FIXED_MARKER.ink}
                      strokeWidth={1.8}
                      strokeLinecap="round"
                    />
                    <path
                      d={FIXED_MARKER.body}
                      fill={FIXED_MARKER.ink}
                      stroke="#ffffff"
                      strokeWidth={1}
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              );
            })}

        {bayGrabHandle && (
          <svg
            data-elevation-grab-handle
            width={bayGrabHandle.hitRadius * 2}
            height={bayGrabHandle.hitRadius * 2}
            viewBox={`0 0 ${bayGrabHandle.hitRadius * 2} ${bayGrabHandle.hitRadius * 2}`}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={handleMoveHandleTouchStart}
            onMouseDown={handleMoveHandleMouseDown}
            // A press that never travels is not a click on the background: it
            // must not reach the container's deselect.
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: bayGrabHandle.x - bayGrabHandle.hitRadius,
              top: bayGrabHandle.y - bayGrabHandle.hitRadius,
              overflow: 'visible',
              touchAction: 'none',
              cursor: 'grab',
              zIndex: 20,
            }}
          >
            {/* Tether back to the object, so the pairing is unmistakable. */}
            <line
              x1={bayGrabHandle.hitRadius}
              y1={bayGrabHandle.hitRadius}
              x2={bayGrabHandle.hitRadius}
              y2={bayGrabHandle.hitRadius - bayGrabHandle.tetherPx}
              stroke="rgba(0, 102, 255, 0.55)"
              strokeWidth={1.5 / (zoomScale || 1)}
              strokeDasharray={`${4 / (zoomScale || 1)} ${3 / (zoomScale || 1)}`}
            />
            <circle
              cx={bayGrabHandle.hitRadius}
              cy={bayGrabHandle.hitRadius}
              r={bayGrabHandle.radius}
              fill="#0066ff"
              stroke="#ffffff"
              strokeWidth={2 / (zoomScale || 1)}
            />
            {/* A two-way arrow ALONG the wall — the one axis a bay can travel,
                where the plan's move handle shows all four. */}
            <path
              d={arrowPath(
                bayGrabHandle.hitRadius,
                bayGrabHandle.hitRadius,
                bayGrabHandle.radius * 0.55,
                bayGrabHandle.radius * 0.22
              )}
              stroke="#ffffff"
              strokeWidth={2 / (zoomScale || 1)}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
        {projectedObjects.map((obj, objIndex) => {
          const opacity = 1 - (obj.distanceFromWall / 1.0) * 0.5; // Fade based on distance

          // Get projections for this object - try multiple keys
          const objType = obj.type || obj.category;
          const objName = obj.name;
          const objIdentifier = obj.id;

          // Check if this object has a modelId assigned directly
          const modelId = obj.modelId;

          // Determine the type key for finding GLB objects
          const typeKey =
            typeof objType === 'object' && objType
              ? Object.keys(objType)[0]
              : objType;

          // Same resolution the label-placement pass uses — one definition, so
          // the drawing and the occupancy grid cannot disagree about what is
          // drawn. The type default is for an object that names NO model; see
          // `projectionsForObject`.
          const projections = projectionsForObject(
            obj,
            objectProjections,
            glbObjects,
            typeKey
          );

          if (projections && projections.front) {
            // Determine which view to show based on wall orientation
            const wallNormal = getWallNormal(wallData);

            // Get object position from center
            let objPos;
            if (obj.center) {
              if (Array.isArray(obj.center)) {
                objPos = new THREE.Vector3(
                  obj.center[0],
                  obj.center[1],
                  obj.center[2]
                );
              } else if (typeof obj.center === 'object') {
                objPos = new THREE.Vector3(
                  obj.center.x || 0,
                  obj.center.y || 0,
                  obj.center.z || 0
                );
              } else {
                objPos = new THREE.Vector3(0, 0, 0);
              }
            } else {
              objPos = new THREE.Vector3(0, 0, 0);
            }

            const wallPos = getWallPosition(wallData);
            const objectToWall = wallPos.clone().sub(objPos);

            // Get object rotation
            let objRotX = 0,
              objRotY = 0,
              objRotZ = 0;
            if (obj.rotation) {
              if (Array.isArray(obj.rotation)) {
                [objRotX, objRotY, objRotZ] = obj.rotation;
              } else if (typeof obj.rotation === 'object') {
                objRotX = obj.rotation.x || 0;
                objRotY = obj.rotation.y || 0;
                objRotZ = obj.rotation.z || 0;
              }
            }
            // Auto-detect if values are in degrees or radians
            const maxObjRot = Math.max(
              Math.abs(objRotX),
              Math.abs(objRotY),
              Math.abs(objRotZ)
            );
            if (maxObjRot > 2 * Math.PI) {
              objRotX = THREE.MathUtils.degToRad(objRotX);
              objRotY = THREE.MathUtils.degToRad(objRotY);
              objRotZ = THREE.MathUtils.degToRad(objRotZ);
            }
            const objRotation = new THREE.Euler(objRotX, objRotY, objRotZ);

            // Get object dimensions - prioritize actual object dimensions, fallback to GLB
            let objWidth;
            let objHeight;
            let objDepth;
            let glbRotation = null; // Store the GLB object's rotation

            // Find the matching GLB object to get rotation and dimensions
            const typeKey =
              typeof objType === 'object' && objType
                ? Object.keys(objType)[0]
                : objType;

            let matchedGlbObj = null;
            if (glbObjects[typeKey]) {
              // Find GLB object with matching projections
              const glbObj = glbObjects[typeKey].find((g) => {
                if (!g.edgeProjections || !projections) return false;
                // Check if any projection URL matches
                return Object.values(g.edgeProjections).some((ep) =>
                  Object.values(projections).some((p) => p.url === ep.url)
                );
              });

              if (glbObj) {
                matchedGlbObj = glbObj;

                // Get rotation if available
                if (glbObj.rotation) {
                  glbRotation = glbObj.rotation;
                }

                // Use GLB dimensions - check unit to avoid double-scaling
                if (glbObj.dimensions) {
                  const sf =
                    glbObj.dimensions.unit === 'inches'
                      ? 1
                      : glbObj.scaleFactor || 1;
                  objWidth =
                    (glbObj.dimensions.width || glbObj.dimensions[0]) *
                    sf *
                    0.0254;
                  objHeight =
                    (glbObj.dimensions.height || glbObj.dimensions[1]) *
                    sf *
                    0.0254;
                  objDepth =
                    (glbObj.dimensions.depth || glbObj.dimensions[2]) *
                    sf *
                    0.0254;
                } else if (glbObj.originalDimensions) {
                  const sf = glbObj.scaleFactor || 1;
                  objWidth = glbObj.originalDimensions.width * sf * 0.0254;
                  objHeight = glbObj.originalDimensions.height * sf * 0.0254;
                  objDepth = glbObj.originalDimensions.depth * sf * 0.0254;
                }
              }
            }

            // Skip object if dimensions are still missing (instead of crashing)
            if (!objWidth || !objHeight || !objDepth) {
              console.warn(
                '[RenderWallElevation] Skipping object with missing dimensions:',
                {
                  objId: obj.id,
                  objType: typeKey,
                  modelId,
                  hasDimensions: !!obj.dimensions,
                  hasMatchedGlb: !!matchedGlbObj,
                }
              );
              return null;
            }

            const viewName = getProjectionView(wallNormal, objRotation);
            // `back` never falls back to the front image: a model with no
            // back projection drops through to the schematic / silhouette
            // path below rather than showing its face from behind.
            const projection =
              viewName === 'back'
                ? projections.back
                : projections[viewName] || projections.front;

            if (projection && projection.url) {
              // Note: Do NOT apply applyRotationToDimensions here.
              // All dimension sources (GLB model dimensions and obj.dimensions)
              // store post-rotation visual dimensions (bounding box after rotation).
              // Applying GLB rotation would cause double-rotation and incorrect sizing.
              let rotatedDims = {
                width: objWidth,
                height: objHeight,
                depth: objDepth,
              };

              // Scale SVG container to match object's actual dimensions
              // Convert from meters to pixels using the same scale as the wall
              // Use rotated dimensions for correct mapping
              let projWidth, projHeight;
              if (viewName === 'left' || viewName === 'right') {
                // For side views, depth becomes width
                projWidth = rotatedDims.depth * scale;
                projHeight = rotatedDims.height * scale;
              } else if (viewName === 'top' || viewName === 'bottom') {
                // For top/bottom views, use width and depth
                projWidth = rotatedDims.width * scale;
                projHeight = rotatedDims.depth * scale;
              } else {
                // Front/back views use width and height
                projWidth = rotatedDims.width * scale;
                projHeight = rotatedDims.height * scale;
              }

              const objId = obj.id;
              const isSelected = isSelectedId(objId);
              const isHighlighted = highlightedObjectIds.includes(objId);

              return (
                <div
                  key={obj.id || objIndex}
                  data-elevation-object-id={objId}
                  onPointerDown={(e) => handleObjectPointerDown(e, obj)}
                  onTouchStart={(e) => handleObjectTouchStart(e, obj)}
                  onMouseDown={(e) => handleObjectMouseDown(e, obj)}
                  onClick={(e) => handleObjectClick(e, obj)}
                  style={{
                    position: 'absolute',
                    left: obj.x2D - projWidth / 2,
                    top: obj.y2D - projHeight / 2,
                    width: projWidth,
                    height: projHeight,
                    opacity: opacity,
                    overflow: 'visible',
                    clipPath: ceilingClipPath(
                      obj.ceilingY2D,
                      obj.y2D - projHeight / 2
                    ),
                    cursor: isPanning
                      ? 'grabbing'
                      : isPreview
                        ? 'default'
                        : 'pointer',
                    title: `${obj.type || 'Object'} - ${obj.distanceFromWall.toFixed(2)}m from wall`,
                  }}
                >
                  <InlineSVGProjection
                    url={projection.url}
                    alt={`${obj.type} projection`}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    strokeWidthPx={isCapture ? CAPTURE_STROKE_PX.object : 0.75}
                    style={INLINE_PROJECTION_STYLE}
                  />
                </div>
              );
            }
          }

          // Fallback to simple box if no projection available
          // Get object dimensions for the box - use actual geometry dimensions
          let boxWidth = 50; // Default width in pixels
          let boxHeight = 50; // Default height in pixels

          // Check if this is a door or window early
          const isDoor = obj.category?.door || obj.type === 'door';
          const isWindow = obj.category?.window || obj.type === 'window';

          // Get object rotation for view determination
          let objRotX = 0,
            objRotY = 0,
            objRotZ = 0;
          if (obj.rotation) {
            if (Array.isArray(obj.rotation)) {
              [objRotX, objRotY, objRotZ] = obj.rotation;
            } else if (typeof obj.rotation === 'object') {
              objRotX = obj.rotation.x || 0;
              objRotY = obj.rotation.y || 0;
              objRotZ = obj.rotation.z || 0;
            }
          }
          // Auto-detect if values are in degrees or radians
          const maxObjRot = Math.max(
            Math.abs(objRotX),
            Math.abs(objRotY),
            Math.abs(objRotZ)
          );
          if (maxObjRot > 2 * Math.PI) {
            objRotX = THREE.MathUtils.degToRad(objRotX);
            objRotY = THREE.MathUtils.degToRad(objRotY);
            objRotZ = THREE.MathUtils.degToRad(objRotZ);
          }
          const objRotation = new THREE.Euler(objRotX, objRotY, objRotZ);

          // Determine which view we're looking at to select correct dimensions
          const wallNormal = getWallNormal(wallData);
          const viewName = getProjectionView(wallNormal, objRotation);

          // Hoisted so the schematic dispatch below can pass canonical
          // width/height/depth alongside the `view` prop (the schematic
          // picks which dimension drives its SVG viewBox based on view).
          let objWidth, objHeight, objDepth;
          if (obj.dimensions) {
            // Handle different dimension formats
            if (
              typeof obj.dimensions === 'object' &&
              !Array.isArray(obj.dimensions)
            ) {
              objWidth = obj.dimensions.x || obj.dimensions.width;
              objHeight = obj.dimensions.y || obj.dimensions.height;
              objDepth = obj.dimensions.z || obj.dimensions.depth;
            } else if (Array.isArray(obj.dimensions)) {
              [objWidth, objHeight, objDepth] = obj.dimensions;
            }

            // For doors/windows, provide reasonable defaults if dimensions are missing
            if (isDoor || isWindow) {
              objWidth = objWidth || (isDoor ? 0.9 : 1.2); // Default door 90cm, window 120cm wide
              objHeight = objHeight || (isDoor ? 2.1 : 1.5); // Default door 210cm, window 150cm tall
              objDepth = objDepth || 0.1; // Default 10cm depth
            }

            if (!objWidth || !objHeight || !objDepth) {
              console.warn('Missing object dimensions for:', obj.type, obj);
              // Don't return null for doors/windows, let them use default sizes below
              if (!isDoor && !isWindow) {
                return null;
              }
            }

            // Select the appropriate dimensions based on the view
            // For elevation views, we show width x height projection
            if (viewName === 'left' || viewName === 'right') {
              // Side views show depth x height
              boxWidth = objDepth * scale;
              boxHeight = objHeight * scale;
            } else if (viewName === 'top' || viewName === 'bottom') {
              // Top/bottom views show width x depth
              boxWidth = objWidth * scale;
              boxHeight = objDepth * scale;
            } else {
              // Front/back views show width x height
              boxWidth = objWidth * scale;
              boxHeight = objHeight * scale;
            }
          }

          // Use actual dimensions without minimum

          const objId = obj.id;
          const isSelected = isSelectedId(objId);
          const isHighlighted = highlightedObjectIds.includes(objId);

          // Stroke conventions are owned by the caller: schematics receive
          // resolved strokeColor + strokeWidth so selection / capture-mode
          // wiring stays in one place, regardless of which tier owns the
          // component (v1 doors+windows, v2 appliances, …).
          // In RUN scope every member of the run is the edit target, so the
          // pink names the run rather than one box inside it — the same paint
          // the floor plan applies via `objectPrimaryHighlight`.
          const inRunScope = runScopeIds.includes(objId);
          const strokeColor =
            isSelected || inRunScope ? HIGHLIGHT_COLORS.PRIMARY : 'black';
          const strokeWidth =
            isSelected || inRunScope || isHighlighted
              ? isCapture
                ? CAPTURE_STROKE_PX.object * EMPHASIS_STROKE_FACTOR
                : 1.5
              : isCapture
                ? CAPTURE_STROKE_PX.object
                : 0.75;

          let backgroundColor = 'transparent';
          let borderStyle = 'none';
          let content;

          const SchematicComponent = getSchematicComponent(obj);
          const widthMeters = boxWidth / scale;
          const heightMeters = boxHeight / scale;
          // Map the projection-view result onto the views a procedural
          // schematic understands: front | back | left | right.
          //
          // `back` used to fall back to front, on the reading that a back
          // face reads close enough. On an island or peninsula's far side
          // it does not: the drawing put the run's doors, pulls, sink and
          // dishwasher controls on what is actually a finished back panel,
          // so Side B came out as Side A mirrored. A schematic that can
          // draw its back says so with `supportsBackView`; one that cannot
          // falls to the SILHOUETTE below (`schematicView === null`), which
          // states the extent without inventing a face.
          //
          // `top` / `bottom` still fall back to front — a wall elevation
          // rarely surfaces those, and neither is a face-bearing view.
          // An L corner unit on one of its own legs shows that leg's FRONT,
          // whichever way its single rotation points (`cornerFront`).
          const schematicView = obj.cornerFront
            ? 'front'
            : viewName === 'left' || viewName === 'right'
              ? viewName
              : viewName === 'back' && SchematicComponent?.supportsBackView
                ? 'back'
                : viewName === 'back'
                  ? null
                  : 'front';
          // Phase C: if the requested view is a side and the registered
          // schematic doesn't opt in to side rendering, fall through to
          // the generic rect instead of forcing a front-view drawing
          // into a side-shaped container. Mirrors the GLB pipeline's
          // `projections[viewName] || projections.front` graceful
          // fallback, only here the fallback is the bbox stroke.
          const canRenderSchematic =
            SchematicComponent &&
            schematicView !== null &&
            (schematicView === 'front' ||
              schematicView === 'back' ||
              SchematicComponent.supportsSideViews === true);
          if (canRenderSchematic) {
            content = (
              <SchematicComponent
                obj={obj}
                catalogModel={catalogModelForObject(obj, glbObjects, typeKey)}
                widthMeters={
                  obj.cornerFront === 'B'
                    ? (objDepth ?? widthMeters)
                    : (objWidth ?? widthMeters)
                }
                heightMeters={objHeight ?? heightMeters}
                depthMeters={objDepth}
                view={schematicView}
                strokeColor={strokeColor}
                strokeWidth={strokeWidth}
                isCapture={isCapture}
              />
            );
          } else {
            // Generic fallback for types without a schematic yet (v2/v3+).
            // Uses a viewBox in meters so the rect always fills the
            // already-sized absolute container above.
            //
            // Demoed-opening branch: a demoed opening (category.opening.demoed
            // === true) is a cased opening with no schematic, so it lands here.
            // When showDemoedWalls is ON we fill its rect with a red 45°
            // diagonal hatch (matching the floorplan + 3D demoed indication)
            // plus a thin red outline. When OFF — or for any non-demoed
            // object — this renders exactly as before (transparent fill,
            // black/selection stroke). Each elevation object owns its own
            // <svg> root, so the <pattern> def lives inside this same SVG with
            // an object-scoped id; cross-SVG url() refs would not resolve.
            const isDemoed = isDemoedOpening(obj) && showDemoedWalls;
            // Cased openings (archways / pass-throughs / demoed sections) — fill
            // them lightly gray so the gap reads as an opening. Demoed ones take
            // the red hatch instead (checked first below).
            const isOpening = obj.type === 'opening' || !!obj.category?.opening;
            const patternId = `demoed-hatch-${obj.id || objIndex}`;
            // Hatch tile sized in meters so density reads consistently across
            // scales (the viewBox units are meters). One diagonal line family
            // at 45° = the standard "marked for demolition" hatch.
            const tile = 0.06; // 6cm tile in world units
            content = (
              <svg
                viewBox={`0 0 ${widthMeters} ${heightMeters}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '100%', height: '100%', overflow: 'visible' }}
              >
                {isDemoed && (
                  <defs>
                    <pattern
                      id={patternId}
                      patternUnits="userSpaceOnUse"
                      width={tile}
                      height={tile}
                      patternTransform="rotate(45)"
                    >
                      {/* Faint translucent red wash behind the lines, matching
                          the floorplan's light-red background. */}
                      <rect
                        x={0}
                        y={0}
                        width={tile}
                        height={tile}
                        fill={DEMOED_HATCH_COLOR}
                        fillOpacity={0.12}
                      />
                      <line
                        x1={0}
                        y1={0}
                        x2={0}
                        y2={tile}
                        stroke={DEMOED_HATCH_COLOR}
                        strokeWidth={tile * 0.18}
                      />
                    </pattern>
                  </defs>
                )}
                <rect
                  x={0}
                  y={0}
                  width={widthMeters}
                  height={heightMeters}
                  fill={
                    isDemoed
                      ? `url(#${patternId})`
                      : isOpening
                        ? 'rgba(120, 120, 120, 0.18)'
                        : 'none'
                  }
                  stroke={isDemoed ? DEMOED_HATCH_COLOR : strokeColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            );
          }

          return (
            <div
              key={obj.id || objIndex}
              data-elevation-object-id={obj.id}
              onPointerDown={(e) => handleObjectPointerDown(e, obj)}
              onTouchStart={(e) => handleObjectTouchStart(e, obj)}
              onMouseDown={(e) => handleObjectMouseDown(e, obj)}
              onClick={(e) => handleObjectClick(e, obj)}
              style={{
                position: 'absolute',
                left: obj.x2D - boxWidth / 2,
                top: obj.y2D - boxHeight / 2,
                width: boxWidth,
                height: boxHeight,
                // A schematic may draw above its own box (a hood's duct, a
                // stove's flue) — the ceiling is where that stops.
                clipPath: ceilingClipPath(
                  obj.ceilingY2D,
                  obj.y2D - boxHeight / 2
                ),
                backgroundColor: backgroundColor,
                opacity: 1, // Full opacity for line drawings
                border: borderStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: isDoor || isWindow ? 'transparent' : '#333', // Show letter for objects without projections
                cursor: isPanning ? 'grabbing' : 'pointer',
                borderRadius: '0',
                overflow: 'visible', // Allow SVG to render fully
                title: `${obj.type || 'Object'} - ${obj.distanceFromWall.toFixed(2)}m from wall`,
              }}
            >
              {content}
            </div>
          );
        })}

        {/* Render labels overlay */}
        {showLabels &&
          (() => {
            // Build occupancy grids for SVG-aware label placement
            const occupancyGrids = new Map();
            const borderGrid = buildBorderGrid();
            projectedObjects.forEach((obj) => {
              const url = resolveObjectSvgUrl(
                obj,
                objectProjections,
                glbObjects,
                wallData
              );
              if (url) {
                const grid = getOccupancyGrid(url, getSvgCache());
                if (grid) {
                  occupancyGrids.set(obj.id, grid);
                  return;
                }
              }
              // Fallback box elements (not doors/windows) get a border-only grid
              // so labels can be placed inside their outlined rectangles
              const isDoor = obj.category?.door || obj.type === 'door';
              const isWindow = obj.category?.window || obj.type === 'window';
              if (!isDoor && !isWindow) {
                occupancyGrids.set(obj.id, borderGrid);
              }
            });

            const placements = computeLabelPlacements(
              projectedObjects,
              scale,
              isPreview,
              occupancyGrids
            );
            const labelFontSize = isPreview ? 8 : 11;
            return (
              <>
                {/* Leader lines */}
                <svg
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    overflow: 'visible',
                    zIndex: 15,
                  }}
                >
                  {placements
                    .filter((p) => p.needsLine)
                    .map((p) => {
                      // C-shaped curved connector: label left → bulge left → anchor
                      const elbowOffset = 8;
                      const labelLeft = p.x - p.labelW / 2;
                      const bulgeX =
                        Math.min(labelLeft, p.anchorX) - elbowOffset;
                      // Cubic Bezier: control points pulled left to form C-shape
                      const d = `M${labelLeft},${p.y} C${bulgeX},${p.y} ${bulgeX},${p.anchorY} ${p.anchorX},${p.anchorY}`;
                      return (
                        <path
                          key={`line-${p.id}`}
                          d={d}
                          fill="none"
                          stroke="#888"
                          strokeWidth={
                            isCapture ? CAPTURE_STROKE_PX.annotation : 0.5
                          }
                        />
                      );
                    })}
                </svg>
                {/* Label text */}
                {placements.map((p) => (
                  <div
                    key={`label-${p.id}`}
                    style={{
                      position: 'absolute',
                      left: p.x,
                      top: p.y,
                      transform: 'translate(-50%, -50%)',
                      fontSize: labelFontSize,
                      fontWeight: 500,
                      color: '#555',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 16,
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      padding: '1px 4px',
                      borderRadius: 2,
                    }}
                  >
                    {p.label}
                  </div>
                ))}
              </>
            );
          })()}

        {/* Render measurements as SVG overlay - only for primary wall */}
        {showMeasurements && isPrimary && (
          <svg
            ref={svgRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none', // SVG itself doesn't capture clicks
              overflow: 'visible',
              zIndex: 10, // Ensure measurements are on top
              cursor: isDragging ? 'grabbing' : 'auto',
            }}
          >
            {(() => {
              // Tiered strings are the default and the only thing that prints;
              // `all` is the older per-type chain generator, kept as a viewing
              // mode. See claude-docs/2026-09-17-elevation-tiered-dimensions.md
              const isTiered = measurementFilter !== 'all';
              const allMeasurements = isTiered
                ? buildTieredElevationMeasurements(
                    wallData,
                    wallSegments,
                    projectedObjects,
                    scale,
                    // In a capture those offsets are PAPER, so print stands the
                    // dimensions closer to the drawing than the screen does.
                    isCapture ? TIER_GEOMETRY.capture : TIER_GEOMETRY.screen
                  )
                : calculateElevationMeasurements(
                    wallData,
                    wallSegments,
                    projectedObjects,
                    measurementFilter,
                    scale // Pass the actual scale being used for this wall
                  );

              // Split into what the drawing carries and what the user has
              // hidden. The hidden half is normally dropped; with
              // `showHiddenMeasurements` on it comes back as grey ghosts.
              // The rule lives in `elevationEditOps` — the copy that used to
              // sit here drifted from the one the Hide handler writes with.
              const isMeasurementVisible = (measurement) =>
                !isMeasurementHidden(
                  removedMeasurements,
                  measurement,
                  wallData.id
                );

              const measurements = allMeasurements.filter(isMeasurementVisible);
              const hiddenMeasurements = showHiddenMeasurements
                ? allMeasurements.filter((m) => !isMeasurementVisible(m))
                : [];

              // Runs before user overrides so that any user-dragged offset
              // (measurementOffsets[key]) still wins.
              const labelFontPx = measurementFontSize || (isPreview ? 10 : 14);
              const measurementScale = isPreview ? 1 : zoomScale;
              // The size the label is actually DRAWN at — `fontSize /
              // max(zoom, 2)` in createMeasurementSVGElements. Measuring the
              // requested size instead reserved twice the room every label
              // needs, which pushed rows outward for nothing.
              const drawnFontPx = labelFontPx / Math.max(measurementScale, 2);
              const staggered = isTiered
                ? // Tiered: the line stays on its tier, only the label moves —
                  // and a line whose labels cannot fit at this scale is given
                  // up entirely rather than printed as a tangle.
                  placeStringLabels(
                    fitStringsToScale(measurements, { fontPx: drawnFontPx }),
                    { fontPx: drawnFontPx }
                  )
                : // All: overlapping dim lines push outward into new rows.
                  layoutElevationMeasurements(measurements, {
                    fontPx: drawnFontPx,
                  });

              // Apply user-dragged offsets on top of the auto-staggered layout.
              const measurementsWithOffsets = staggered.map((m) => {
                return { ...m, offset: offsetFor(m, m.offset || 0) };
              });

              // Create measurement SVG elements with appropriate scale and
              // handlers. Note: 'scale' above is the local pixels-per-meter
              // variable; `measurementScale` is the zoom.
              const svgElements = createMeasurementSVGElements(
                measurementsWithOffsets,
                measurementScale, // Pass zoom scale to inversely scale measurements
                {
                  fontSize: labelFontPx,
                  strokeWidth: isCapture ? CAPTURE_STROKE_PX.annotation : 0.5,
                  showLabels: true,
                  selectedObjectId: selectedObjectId || selectedObject, // Highlight measurements for selected object
                  selectedMeasurement: selectedMeasurementData?.measurement, // Highlight the clicked measurement
                  onMeasurementClick:
                    !isPreview && setSelectedMeasurementData
                      ? (measurement) => {
                          const dataToSet = {
                            measurement,
                            wallIdentifier: wallData.id || `Wall ${index + 1}`,
                            roomName: wallData.roomName || roomName,
                          };
                          setSelectedMeasurementData(dataToSet);
                          setSelectedObject(null);
                          setSelectedObjectData(null);
                          if (onObjectSelect) onObjectSelect(null);
                        }
                      : null,
                  onMeasurementMouseDown: handleMeasurementMouseDown,
                  onLabelMouseDown: handleLabelMouseDown,
                  isDragging: isDragging,
                  draggedIndex: draggedMeasurement?.index,
                }
              );

              // Ghosts for the hidden ones, staggered OUTBOARD of the visible
              // dim lines so revealing them never shifts the real drawing. A
              // user-dragged offset still wins, so a ghost sits exactly where
              // restoring it would put it.
              // Tiered strings never re-pack, so a hidden segment's ghost sits
              // in the gap it left on its own line. The `all` mode still has
              // to push its ghosts outboard of the rows it packed.
              const hiddenWithOffsets = (
                isTiered
                  ? placeStringLabels(
                      fitStringsToScale(hiddenMeasurements, {
                        fontPx: drawnFontPx,
                      }),
                      { fontPx: drawnFontPx }
                    )
                  : layoutHiddenElevationMeasurements(
                      staggered,
                      hiddenMeasurements,
                      { fontPx: drawnFontPx }
                    )
              ).map((m) => {
                return { ...m, offset: offsetFor(m, m.offset || 0) };
              });

              const hiddenElements = hiddenWithOffsets.length
                ? createHiddenMeasurementSVGElements(
                    hiddenWithOffsets,
                    measurementScale,
                    {
                      fontSize: labelFontPx,
                      strokeWidth: isCapture
                        ? CAPTURE_STROKE_PX.annotation
                        : 0.5,
                    }
                  )
                : [];

              return [...hiddenElements, ...svgElements].map((element) => {
                if (element.type === 'line') {
                  return <line key={element.key} {...element.props} />;
                } else if (element.type === 'rect') {
                  return <rect key={element.key} {...element.props} />;
                } else if (element.type === 'path') {
                  return <path key={element.key} {...element.props} />;
                } else if (element.type === 'circle') {
                  return <circle key={element.key} {...element.props} />;
                } else if (element.type === 'text') {
                  return (
                    <text key={element.key} {...element.props}>
                      {element.content}
                    </text>
                  );
                }
                return null;
              });
            })()}
          </svg>
        )}
      </div>
    </div>
  );
};

export default RenderWallElevation;
